import { useState, useEffect, useRef } from 'react'
import { getGames, getGamesByDate, getPlayoffGames, isGameLocked, spreadLabel, fmtML, PLAYOFF_ROUND_INFO, getUFCEventState, isMainCardEligible, hasMainCardDefinition } from '../lib/odds'
import { getActiveRound } from '../lib/playoffs'
import { savePicks, loadPicks, getDayKey, resolvePicks } from '../lib/db'
import TeamComparison from './TeamComparison'
import { getBoxScore } from '../lib/boxScores'
import { NEUTRAL_BG, NEUTRAL_TEXT, pickFill, pickLabel, fighterInitial, iconicTeamColor } from '../lib/teamColors'
import { log } from '../lib/log'

// Sports that get the team-color-driven HeroCard treatment for regular-
// season upcoming games. Playoff games (any sport) and live/final games
// fall through to the shared GameCard layout below. NFL hero treatment
// isn't built yet; UFC has no team color data and a different card
// semantic anyway.
const HERO_SPORTS = new Set(['MLB', 'NBA', 'NHL'])

// Sport-aware spread-column label in HeroOdds. NHL convention is
// "PUCK LINE" (fixed-1.5 by tradition); MLB/NBA use "SPREAD". Unknown
// sports fall back to "SPREAD" via the `||` in HeroOdds.
const SPREAD_LABEL_BY_SPORT = { MLB: 'SPREAD', NBA: 'SPREAD', NHL: 'PUCK LINE' }

// UFC corner colors — FIXED by position, not data-driven. UFC has no
// team-color field; the red/blue corner convention is the genre's
// visual identity. Left card (away by app convention) = red; right
// card (home) = blue. Matching fighter silhouette webp images live in
// /public/ufc/ pre-split per side.
// UFC corner colors + FLAG_PALETTES re-exported from the shared lib
// (src/lib/ufcFighterPanel.js) so PickemMode and the Games-tab UFC
// cards share one source of truth. Original definitions moved out
// during the Games-tab port; the re-export preserves local-import
// patterns at the existing call sites.
import {
  UFC_CORNER_RED,
  UFC_CORNER_BLUE,
  FLAG_PALETTES,
  cornerTintBg as ufcCornerTintBg,
} from '../lib/ufcFighterPanel'
import FighterPanelLayers from './FighterPanelLayers'

// FLAG_PALETTES re-export — sourced from src/lib/ufcFighterPanel.js
// (single source of truth shared with the Games-tab UFC cards). The
// existing call sites below reference FLAG_PALETTES as a local
// identifier; the named import at the top of this file makes that
// reference resolve through the shared module.

// Countdown to pick lock (10 minutes before commenceTime).
// `style` is an optional inline-style override (used by HeroCard's lock row
// for a slightly larger / different-color rendering); existing call sites pass
// nothing and behave identically.
function Countdown({ commenceTime, style }) {
  const [timeLeft, setTimeLeft] = useState('')
  const [color, setColor] = useState('var(--gold)')
  useEffect(() => {
    if (!commenceTime) return
    const update = () => {
      const now = Date.now()
      const lockTime = new Date(commenceTime).getTime() - 10 * 60 * 1000
      const diff = lockTime - now
      if (diff <= 0) {
        setTimeLeft('🔒 LOCKED')
        setColor('var(--text-muted)')
        return
      }
      const h = Math.floor(diff / 3600000)
      const m = Math.floor((diff % 3600000) / 60000)
      const s = Math.floor((diff % 60000) / 1000)
      if (h > 0)      setTimeLeft(`Locks in ${h}h ${m}m`)
      else if (m > 0) setTimeLeft(`Locks in ${m}m ${s}s`)
      else            setTimeLeft(`Locks in ${s}s`)
      setColor(diff < 30 * 60 * 1000 ? 'var(--red)' : 'var(--gold)')
    }
    update()
    const interval = setInterval(update, 1000)
    return () => clearInterval(interval)
  }, [commenceTime])
  return <span style={{ color, fontSize:'11px', fontFamily:'Arial,sans-serif', fontWeight:'bold', ...style }}>{timeLeft}</span>
}

// Line movement arrow next to spread
function LineArrow({ game, side }) {
  if (!game.lineMovement || game.lineMovement === 'none') return null
  // game.lineMovement: 'up' = home favored more; 'down' = away favored more.
  // Movement *toward* this side = green; movement *away* = red.
  const towardThisSide =
    (game.lineMovement === 'up'   && side === 'home') ||
    (game.lineMovement === 'down' && side === 'away')
  const arrow = towardThisSide ? '↑' : '↓'
  const color = towardThisSide ? 'var(--green)' : 'var(--red)'
  return (
    <span title={`Line moved ${game.movementAmount} since open`}
      style={{ color, fontSize:'11px', marginLeft:'4px', fontFamily:'Arial,sans-serif', fontWeight:'bold' }}>
      {arrow}
    </span>
  )
}

// Team logo with abbreviation fallback (shared by team buttons).
// Owned/licensed team badge — colored chip with the team's abbreviation for
// team sports, neutral chip with the fighter's shortName for UFC. No ESPN-
// hotlinked images. Color/contrast pipeline imported from the shared
// teamColors module (single source of truth across TeamLogoImg + this one);
// PickemMode keeps its own component for the per-card margin/ring semantics.
function TeamLogo({ team, abbr, sport, color, alternateColor, shortName, size = 36, borderColor = 'transparent', isPicked }) {
  const isUFC = sport === 'UFC'
  const ring  = isPicked ? 'var(--gold)' : borderColor

  let bg, labelColor, label, fontSize
  if (isUFC) {
    // Single uppercase initial from the fighter's last name — matches the
    // team-sport chip's visual weight at the same size ratio. The fighter's
    // full name still renders alongside the chip in the row text.
    bg         = NEUTRAL_BG
    labelColor = NEUTRAL_TEXT
    label      = fighterInitial(shortName || team)
    fontSize   = Math.max(10, Math.round(size * 0.36))
  } else {
    bg         = pickFill(color, alternateColor)
    labelColor = pickLabel(bg)
    label      = (abbr && abbr.trim()) || ((team || '').split(' ').pop() || '???').slice(0, 3).toUpperCase()
    fontSize   = Math.max(10, Math.round(size * 0.36))
  }

  return (
    <div className="team-logo"
      title={team || ''}
      style={{
        margin: '0 auto 7px',
        width:  size,
        height: size,
        borderRadius: '50%',
        background: bg,
        color: labelColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Arial,sans-serif',
        fontWeight: 700,
        fontSize,
        letterSpacing: isUFC ? 0 : 0.5,
        border: ring !== 'transparent' ? `2px solid ${ring}` : 'none',
      }}
    >
      {label}
    </div>
  )
}

// ── Pick data format (stored per game) ────────────────────────────────────────
// { side: 'away'|'home', status: 'pending'|'correct'|'incorrect', pointsAwarded: number }
//
// Grading is server-only. The client never marks a pick correct/incorrect —
// ESPN sometimes flags games as 'final' before they actually finish, which
// produced premature 'incorrect' results. Status transitions arrive via
// loadPicks reading the picks table after /api/grade-picks has run.

function countByStatus(picks, status) {
  return Object.values(picks).filter(p => p?.status === status).length
}

// Tier badge text — "5 PTS" or "1 PT". Single-sourced so the legend and
// the collapsible section headers can't drift on the plural rule.
const ptsLabel = pts => `${pts} PT${pts !== 1 ? 'S' : ''}`

// UFC tier names keyed by point value. Used by both the legend (implicitly,
// via UFCPointLegend's `tiers` array below) and the section headers in
// DailyUI. Title (7) only renders if a title fight is present on the card.
const UFC_TIER_LABELS = {
  7: 'Title',
  5: 'Main Event',
  3: 'Co-Main',
  2: 'Main Card',
  1: 'Prelims',
}

// Point badge color tiers — applies to UFC fight points (1/2/3/5/7) and to
// bracket-mode playoff round points (1/2/4/8). Higher value → hotter color:
//   1pt   → grey (UFC prelim; hidden on bracket via the `> 1` gate)
//   2     → cyan (UFC main card / bracket R2)
//   3     → blue (UFC co-main)
//   4-5   → gold (UFC main event / bracket conf finals)
//   6+    → red  (UFC title fight / bracket championship)
function roundPtBadgeStyle(pts) {
  if (pts >= 6) return { background:'#dc3545',     color:'#fff'        }
  if (pts >= 4) return { background:'var(--gold)', color:'#000'     }
  if (pts >= 3) return { background:'#3b82f6',     color:'#fff'        }
  if (pts >= 2) return { background:'#0891b2',     color:'#fff'        }
  return        { background:'var(--surface2)', color:'var(--text-dim)', border:'1px solid var(--border)' }
}

// UFC-specific badge style — matches the operator's mock palette
// (gold-amber 7pt Title, dark-green 5pt Main Event). The bracket
// path keeps roundPtBadgeStyle so bracket 4pt stays gold + 8pt stays
// red unchanged.
function ufcPtBadgeStyle(pts) {
  if (pts >= 7) return { background:'#F5B800',     color:'#0d1016' }  // Title — gold/amber
  if (pts >= 5) return { background:'var(--green)', color:'#0d1016' } // Main Event — green
  if (pts >= 3) return { background:'#3b82f6',     color:'#fff'    }  // Co-main — blue
  if (pts >= 2) return { background:'#0891b2',     color:'#fff'    }  // Main Card — cyan
  return        { background:'var(--surface2)', color:'var(--text-dim)', border:'1px solid var(--border)' }
}

// Four-tier legend pinned below the UFC filter tabs. Shares roundPtBadgeStyle
// with the per-fight chips so users learn the colors once and recognize them
// on every card.
function UFCPointLegend() {
  const tiers = [
    { pts: 1, label: 'Prelim'    },
    { pts: 2, label: 'Main Card' },
    { pts: 3, label: 'Co-Main'   },
    { pts: 5, label: 'Main'      },
    { pts: 7, label: 'Title'     },
  ]
  return (
    <div style={{
      display:'flex', gap:10, flexWrap:'wrap', justifyContent:'center',
      marginBottom:12, padding:'8px 12px',
      background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:8,
    }}>
      {tiers.map(t => (
        <div key={t.pts} style={{ display:'flex', alignItems:'center', gap:5, flexShrink:0 }}>
          <span style={{
            fontSize:9, padding:'2px 5px', borderRadius:3,
            fontWeight:'bold', fontFamily:'Arial,sans-serif',
            ...ufcPtBadgeStyle(t.pts),
          }}>
            {ptsLabel(t.pts)}
          </span>
          <span style={{ fontSize:10, color:'var(--text-muted)', fontFamily:'Arial,sans-serif' }}>
            {t.label}
          </span>
        </div>
      ))}
    </div>
  )
}

// Map raw getPlayoffGames shape → GameCard-compatible shape.
const ROUND_NUM_BY_ABBR    = { RD16: 1, RD8: 2, RD4: 3, RD2: 4 }
const ESPN_STATE_TO_STATUS = { pre: 'upcoming', in: 'live', post: 'final' }

function adaptPlayoffGame(g) {
  const abbr      = name => (name?.split(' ').pop().slice(0, 3) || '').toUpperCase()
  const gameNumDigits = (g.gameNumber || '').match(/\d+/)?.[0] || null
  return {
    id: g.id,
    sport: g.sport,
    status: ESPN_STATE_TO_STATUS[g.status] || 'upcoming',
    round: ROUND_NUM_BY_ABBR[g.roundAbbr] || null,
    roundAbbr: g.roundAbbr,
    roundName: g.roundName,
    roundPts: g.roundPoints,
    isPlayoff: g.isPlayoff,
    gameNumber: gameNumDigits,
    seriesInfo: g.seriesRecord,
    away: { team: g.away, abbr: abbr(g.away), score: null, record: '', seed: g.awaySeed },
    home: { team: g.home, abbr: abbr(g.home), score: null, record: '', seed: g.homeSeed },
    startTime: g.time,
    startTimeMs: new Date(g.commenceTime).getTime(),
    spread: null, awayML: null, homeML: null, total: null,
  }
}

// Filter a games array to only the games that fall on a group's start_date.
// No-op for playoff mode or when start_date is absent (shows all games).
function filterByGroupDate(games, startDate, isPlayoffMode) {
  if (isPlayoffMode || !startDate) return games
  // Local-time YYYY-MM-DD via toLocaleDateString('en-CA') — matches the
  // basis used by group.start_date (Fix 3), getDayKey, and the gameDate
  // write chain in handlePick / GameDetailSheet.submitPick (Fix 1b). The
  // previous implementation extracted UTC date via commenceTime regex
  // (and toISOString().slice(0,10) fallback) and compared against the
  // now-local startDate — for events at/after 8 PM EDT (≥ midnight UTC),
  // the UTC date bucketed to the next day and the game was excluded.
  // Fourth instance of the local-vs-UTC fault line (full audit pass
  // tracked as post-dry-run cleanup).
  //
  // Preserves Fix 2's "exclude undatable" behavior: games with neither
  // commenceTime nor a valid startTimeMs return false (no defensive
  // `return true` reintroduction).
  return games.filter(g => {
    const dateSource = g.commenceTime
                    || (g.startTimeMs && !Number.isNaN(g.startTimeMs) ? g.startTimeMs : null)
    if (!dateSource) return false  // no derivable date → not pickable
    return new Date(dateSource).toLocaleDateString('en-CA') === startDate
  })
}

// ── Main component ────────────────────────────────────────────────────────────

export default function PickemMode({
  group, user, members, onPointsUpdate, gradeKey = 0, promptAuth = () => {},
  // Pattern #2 freemium tease (pickem prototype). When `locked` is true,
  // taps still update local pick state (the user SEES their selection —
  // the tease) but the per-tap savePicks call is SKIPPED entirely (no
  // server INSERT, no localStorage write — picks are ephemeral). On
  // handleSubmit, the gate intercepts BEFORE building submitPicks and
  // routes to onUnlockRequest(). A sticky unlock bar (matching
  // GolfDivisionsMode 66f2a6b and GroupChat 52dd228) renders at the
  // bottom. The submit button inside DailyUI/PlayoffUI stays visible —
  // its onClick fires handleSubmit, which gates to onUnlockRequest when
  // locked. 041's RESTRICTIVE policies on picks remain the source of
  // truth. When `locked` is false (default), behavior is byte-identical
  // to pre-Pattern-#2.
  locked = false,
  onUnlockRequest,
  unlocking = false,
  unlockPriceCents = null,
}) {
  const durationType  = group.duration_type || 'daily'
  const isPlayoffMode = durationType === 'playoffs'

  const [games, setGames]         = useState([])
  const [playoffGames, setPlayoffGames] = useState([])
  const [picks, setPicks]         = useState({})
  const [submitted, setSubmitted] = useState(false)    // daily mode: submitted today
  const [roundSubmitted, setRoundSubmitted] = useState({}) // playoff mode: { r1: true }
  const [loading, setLoading]     = useState(true)
  const [tick, setTick]           = useState(0)        // forces lock re-check
  const [toast, setToast]         = useState(null)
  // UFC banner-copy differentiator. Four states:
  //   null               — initial / pre-check (effect hasn't run yet)
  //   'unknown'          — check ran but couldn't get a confirmed answer
  //                        (network blip, timeout, 5xx, missing id).
  //                        Treated as null for render purposes — neutral
  //                        "Checking event status…" panel. Critically NOT
  //                        treated as 'not-found' so a transient blip
  //                        doesn't false-alarm a pre-card pool.
  //   'in-calendar-only' — pre-card wait
  //   'not-found'        — confirmed drift (ESPN responded successfully,
  //                        id was absent from BOTH events[] and calendar[])
  // Only meaningful when group.sport==='UFC' && group.ufc_config?.eventId
  // && games.length===0. Effect below sets it; banner branches on it.
  const [ufcEventState, setUfcEventState] = useState(null)
  const toastTimerRef = useRef(null)
  const resolvedRef  = useRef(false)  // false = initial games load (openGroup handles grading)
  const prevStatusRef = useRef({})     // game_id → status snapshot from last refresh

  function showToast(message) {
    setToast(message)
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
    toastTimerRef.current = setTimeout(() => setToast(null), 2000)
  }

  // Refresh lock state + games every 60 s
  // UFC: pass group.ufc_config?.eventId so the picker fetches the
  // group's selected event (Phase 4 will widen the SELECT to populate
  // this field; until then or for legacy/null configs, getUFCFights
  // falls back to next-event and PickemMode renders the loud banner).
  // dep array MUST include group.ufc_config?.eventId — without it, a
  // mid-session change to ufc_config wouldn't re-fetch and the picker
  // would silently show stale fights.
  useEffect(() => {
    const t = setInterval(async () => {
      setTick(n => n + 1)
      // For dated daily groups, fetch that exact date's scoreboard so
      // future-day groups (start_date > today) actually return games.
      const data = (group.sport !== 'UFC' && group.start_date && !isPlayoffMode)
        ? await getGamesByDate(group.sport, group.start_date)
        : await getGames(group.sport, { eventId: group.ufc_config?.eventId })
      setGames(group.sport === 'UFC' ? data : filterByGroupDate(data, group.start_date, isPlayoffMode))
    }, 60_000)
    return () => clearInterval(t)
  }, [group.sport, group.start_date, group.ufc_config?.eventId, isPlayoffMode])

  // ── UFC event-state check (pre-card vs drift) ──────────────────────────────
  // Fires ONLY when: UFC pool + configured eventId + post-load + zero fights.
  // That's the empty-state moment where the user needs to know whether to
  // wait (pre-card) or worry (drift). Skipped during initial loading and
  // whenever fights are present (the picker handles those states already).
  useEffect(() => {
    const shouldCheck =
      group.sport === 'UFC' &&
      !!group.ufc_config?.eventId &&
      games.length === 0 &&
      !loading
    if (!shouldCheck) {
      setUfcEventState(null)
      return
    }
    let cancelled = false
    getUFCEventState(group.ufc_config.eventId).then(state => {
      if (!cancelled) setUfcEventState(state)
    })
    return () => { cancelled = true }
  }, [group.sport, group.ufc_config?.eventId, games.length, loading])

  // ── Init: load games + saved picks ─────────────────────────────────────────

  useEffect(() => {
    async function init() {
      setLoading(true)
      const data = (group.sport !== 'UFC' && group.start_date && !isPlayoffMode)
        ? await getGamesByDate(group.sport, group.start_date)
        : await getGames(group.sport, { eventId: group.ufc_config?.eventId })
      setGames(group.sport === 'UFC' ? data : filterByGroupDate(data, group.start_date, isPlayoffMode))

      if (isPlayoffMode) {
        // Load picks per round
        const rounds = PLAYOFF_ROUND_INFO[group.sport] || []
        const allPicks = {}
        const submitted = {}
        for (const r of rounds) {
          const saved = await loadPicks(group.id, user.id, r.id)
          if (saved?.picks) Object.assign(allPicks, saved.picks)
          if (saved?.submitted) submitted[r.id] = true
        }
        setPicks(allPicks)
        setRoundSubmitted(submitted)
      } else {
        // Load daily picks — only "submitted today" locks the UI
        const saved = await loadPicks(group.id, user.id, 'daily')
        if (saved?.picks) {
          setPicks(saved.picks)
          if (saved.submitted && saved.dayKey === getDayKey()) {
            setSubmitted(true)
          }
        }
      }
      setLoading(false)
    }
    init()
    // group.ufc_config?.eventId in deps: required so that switching to a
    // different UFC event (or clearing the config) re-fires init. Without
    // this the picker silently shows stale fights for the prior event.
  }, [group.id, user.id, group.sport, group.ufc_config?.eventId, isPlayoffMode, gradeKey])

  // ── Playoff mode: fetch real playoff games from ESPN ──────────────────────
  useEffect(() => {
    if (!isPlayoffMode) return
    let cancelled = false
    ;(async () => {
      const raw = await getPlayoffGames(group.sport)
      if (cancelled) return
      const transformed = raw.map(adaptPlayoffGame)
      setPlayoffGames(transformed)
      // Mirror into games state so pick resolution + locking use the same data
      setGames(transformed)
    })()
    return () => { cancelled = true }
  }, [isPlayoffMode, group.sport])

  // ── Auto-resolve pending picks when games update ────────────────────────────

  useEffect(() => {
    if (games.length === 0) return

    // Build current status snapshot
    const currStatus = {}
    games.forEach(g => { currStatus[g.id] = g.status })

    // After the initial load, call server grading only when a game goes final
    if (resolvedRef.current) {
      const anyNewFinal = games.some(g => {
        const prev = prevStatusRef.current[g.id]
        return g.status === 'final' && prev && prev !== 'final'
      })
      if (anyNewFinal) {
        resolvePicks(group.id, user.id).catch(() => {})
      }
    }

    prevStatusRef.current = currStatus
    resolvedRef.current   = true
  }, [games])

  // ── Pick handler ────────────────────────────────────────────────────────────

  function handlePick(gameId, side, roundId) {
    // Anonymous-browsing gate — each tap fires a savePicks call that needs
    // user.id; pop the Auth overlay and stop if logged out. The local
    // pick state isn't updated either, so a logged-out user doesn't see a
    // phantom selection that would disappear after sign-in.
    if (!user?.id) return promptAuth()
    // Picks remain editable until the per-game lock window (10 min before
    // tip — enforced by isGameLocked). Submit state is intentionally NOT a
    // gate: users can keep adjusting individual picks after pressing
    // Lock In, as long as the specific game hasn't started.
    const game = games.find(g => g.id === gameId)
    if (!game || isGameLocked(game)) return

    // Tapping the team that's already picked is a no-op (no flicker, no save).
    if (picks[gameId]?.side === side) return

    // Main-Card-Only mode: reject picks for fights not in the curated
    // main-card set at SAVE TIME (defense in depth — DailyUI already
    // hides ineligible fights from the picker, but a direct savePicks
    // call via dev tools or an out-of-date client cache could still try
    // to submit a prelim pick). Eligibility keys on fight-ID membership
    // in MAIN_CARD_COMP_IDS, NOT on round_points (which is broken on
    // Freedom 250 due to ALL_MAIN_CARD_EVENT_IDS override — see
    // src/lib/odds.js header comment).
    if (group.contest_type === 'pick_em_main_card_only') {
      const eventId = group.ufc_config?.eventId
      if (!isMainCardEligible(eventId, gameId)) {
        showToast('Prelim picks are not eligible in this pool.')
        return
      }
    }

    // Snapshot game context (team name, opponent, date, round pts) so
    // resolvePicks can match a finished game even if the live game_id
    // has aged out of the ESPN window. Falls through `?. .team` → string
    // form → empty string so home_team/away_team are never null in the
    // saved row (Supabase-side and grader-side both compare strict
    // equality, so null silently kills the match).
    const homeTeamName = game.home?.team || (typeof game.home === 'string' ? game.home : '') || ''
    const awayTeamName = game.away?.team || (typeof game.away === 'string' ? game.away : '') || ''
    const teamName = side === 'away' ? awayTeamName : homeTeamName
    const roundPts = game.roundPts || 1
    // Local-time YYYY-MM-DD via toLocaleDateString('en-CA'), matching the
    // convention used by group.start_date, getDayKey, and filterByGroupDate
    // (Fix 3 + filterByGroupDate ISO comparison). String-splitting
    // commenceTime would yield a UTC day, off-by-one for evening events.
    // Dropping game.startTime from the chain entirely — it's always a
    // display string ('07:05 PM EDT' / 'Final' / 'LIVE'), never a date.
    const dateSource = game.commenceTime
                    || (game.startTimeMs && !Number.isNaN(game.startTimeMs) ? game.startTimeMs : null)
    const gameDate   = dateSource ? new Date(dateSource).toLocaleDateString('en-CA') : null

    const next = {
      ...picks,
      [gameId]: {
        side, team: teamName,
        status: 'pending', pointsAwarded: 0, roundPoints: roundPts,
        homeTeam: homeTeamName, awayTeam: awayTeamName, gameDate,
      },
    }
    setPicks(next)

    // Pattern #2 intercept — locked pool: skip the auto-save entirely.
    // Local pick state still updates (the user sees their selection),
    // but no server INSERT and no localStorage write happens. Picks are
    // ephemeral and will be lost on unlock or page reload. 041's
    // RESTRICTIVE policy on picks would 42501 the INSERT anyway.
    if (!locked) {
      // Auto-save on every pick change. Preserve the existing submitted flag
      // so post-submit edits don't reset the badge / re-enable the submit
      // button on the next render.
      if (isPlayoffMode && roundId) {
        const rGames = games.filter(g => g.round === game.round)
        const rPicks = {}
        rGames.forEach(g => { if (next[g.id]) rPicks[g.id] = next[g.id] })
        savePicks(group.id, user.id, { picks: rPicks, submitted: !!roundSubmitted[roundId] }, roundId)
      } else {
        savePicks(group.id, user.id, { picks: next, submitted: !!submitted, dayKey: getDayKey() }, 'daily')
      }
    }

    showToast(`Pick updated to ${teamName}`)
  }

  // ── Submit handler ──────────────────────────────────────────────────────────

  async function handleSubmit(roundId) {
    // Anonymous-browsing gate — savePicks needs user.id.
    if (!user?.id) return promptAuth()
    // Pattern #2 intercept — locked pool: route to unlock checkout
    // instead of building submitPicks + writing. 041's RESTRICTIVE
    // policy on picks would 42501 the INSERT anyway.
    if (locked) {
      onUnlockRequest?.()
      return
    }
    // Build the picks for this submission
    let submitPicks = {}
    let gameCount   = 0

    if (isPlayoffMode && roundId) {
      const roundInfo = (PLAYOFF_ROUND_INFO[group.sport] || []).find(r => r.id === roundId)
      games.filter(g => g.round === roundInfo?.round && !isGameLocked(g)).forEach(g => {
        if (picks[g.id]) { submitPicks[g.id] = picks[g.id]; gameCount++ }
      })
      if (gameCount === 0) return
      await savePicks(group.id, user.id, { picks: submitPicks, submitted: true }, roundId)
      setRoundSubmitted(prev => ({ ...prev, [roundId]: true }))
      // total_picks now incremented at grade-time by the server (gradePicksForUser).
      // Calling updateMemberPoints here too caused double-counting — removed.
    } else {
      const pickableGames = games.filter(g => !isGameLocked(g))
      pickableGames.forEach(g => {
        if (picks[g.id]) { submitPicks[g.id] = picks[g.id]; gameCount++ }
      })
      if (gameCount === 0) return
      await savePicks(group.id, user.id,
        { picks: submitPicks, submitted: true, dayKey: getDayKey() },
        'daily'
      )
      setSubmitted(true)
      // total_picks now incremented at grade-time by the server (gradePicksForUser).
      // Calling updateMemberPoints here too caused double-counting — removed.
    }
  }

  // ── Loading state ───────────────────────────────────────────────────────────

  if (loading) return (
    <div style={{ textAlign:'center', padding:'40px', color:'var(--text-dim)' }}>
      <div style={{ width:'24px', height:'24px', border:'2px solid var(--border)', borderTopColor:'var(--gold)', borderRadius:'50%', margin:'0 auto 12px', animation:'spin .8s linear infinite' }} />
      Loading games…
    </div>
  )

  // ── start_date guards (future + past) ───────────────────────────────────────
  // Daily groups can be scheduled for a future day or have a past start date
  // where ESPN's data window has rolled over. Compared as YYYY-MM-DD strings
  // (lexicographic == chronological for ISO format). Uses getDayKey (local-
  // time) to match the convention group.start_date is written under post-
  // Fix-3 — previous UTC `toISOString().slice(0,10)` had the same
  // evening-EDT off-by-one as Fix 3 fixed for getDayKey itself.
  if (!isPlayoffMode && group.start_date) {
    const todayIso = getDayKey()
    if (group.start_date < todayIso) {
      // Past day: render a summary from saved pick statuses so the group is
      // still meaningful after the live game data has aged out of getGames().
      const entries  = Object.values(picks)
      const correct  = entries.filter(p => p?.status === 'correct').length
      const wrong    = entries.filter(p => p?.status === 'incorrect').length
      const pending  = entries.filter(p => p?.status === 'pending').length
      const total    = entries.length
      const earnedPts = entries.reduce((s, p) => s + (p?.pointsAwarded || 0), 0)
      const dayLabel = new Date(group.start_date + 'T00:00:00')
        .toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
      return (
        <div>
          <div style={{ background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'14px 16px', marginBottom:'14px', textAlign:'center' }}>
            <div style={{ fontSize:11, color:'var(--text-muted)', letterSpacing:'1px', marginBottom:6, fontFamily:'Arial,sans-serif' }}>
              GROUP DAY · {dayLabel}
            </div>
            <div style={{ fontSize:28, fontWeight:'bold', color:'var(--gold)', fontFamily:'Arial,sans-serif', lineHeight:1 }}>
              {earnedPts} pt{earnedPts !== 1 ? 's' : ''}
            </div>
          </div>
          {total === 0 ? (
            <div style={{ textAlign:'center', padding:'24px 0', color:'var(--text-dim)', fontSize:13 }}>
              No picks were submitted for this group.
            </div>
          ) : (
            <>
              <StatusSummary correct={correct} pending={pending} wrong={wrong} />
              <div style={{ fontSize:11, color:'var(--text-dim)', letterSpacing:'0.8px', margin:'4px 0 8px', fontFamily:'Arial,sans-serif' }}>
                YOUR PICKS
              </div>
              {entries.map((p, i) => {
                const ok      = p.status === 'correct'
                const wrong   = p.status === 'incorrect'
                const teamLbl = p.team || (p.side === 'away' ? 'Away' : p.side === 'home' ? 'Home' : '—')
                const icon    = ok ? '✅' : wrong ? '❌' : '⏳'
                const color   = ok ? 'var(--green)' : wrong ? 'var(--red)' : 'var(--text-dim)'
                return (
                  <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 12px', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:10, marginBottom:6 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0 }}>
                      <span style={{ fontSize:18, flexShrink:0 }}>{icon}</span>
                      <span style={{ fontSize:13, color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        Picked <strong>{teamLbl}</strong>
                      </span>
                    </div>
                    <span style={{ fontSize:13, fontWeight:'bold', color, fontFamily:'Arial,sans-serif', flexShrink:0 }}>
                      {ok ? `+${p.pointsAwarded || 0}pt` : wrong ? '0pt' : '—'}
                    </span>
                  </div>
                )
              })}
            </>
          )}
          <div style={{ textAlign:'center', fontSize:11, color:'var(--text-muted)', marginTop:8 }}>
            Live game data is no longer available · Showing saved results
          </div>
        </div>
      )
    }
    // Future-day groups render games normally so users can lock in picks
    // ahead of time. Per-game lock (10 min before commenceTime) still
    // applies via isGameLocked.
  }

  // ── No games ────────────────────────────────────────────────────────────────

  // UFC empty-fights branch — two distinct reasons, two distinct banners.
  // The condition catches "user configured an event + ESPN has zero fights
  // for it"; the ufcEventState tells us WHY:
  //   null OR 'unknown'  → check in flight OR check failed without a
  //                        confirmed answer (network blip, timeout, etc.).
  //                        Render neutral "Checking event status…" rather
  //                        than picking a banner that might be wrong.
  //                        CRITICAL: 'unknown' must NOT render the warn
  //                        banner — a transient blip on a real pre-card
  //                        pool would otherwise false-alarm "your event is
  //                        gone." Worst case at worst time on launch day.
  //   'in-calendar-only' → reassuring "Picks open closer to fight week"
  //   'not-found'        → warn "This pool's event isn't on the schedule"
  //                        (ESPN-confirmed absence — not a guess)
  const ufcEmptyFights =
    group.sport === 'UFC' &&
    !!group.ufc_config?.eventId &&
    games.length === 0 &&
    !isPlayoffMode

  if (ufcEmptyFights) {
    if (ufcEventState === null || ufcEventState === 'unknown') {
      // null = check pending; 'unknown' = check ran but ESPN didn't
      // give us a confirmed answer (5xx, timeout, network error).
      // Render the neutral placeholder — never let a transient blip
      // surface as the alarming drift banner. User can refresh the page
      // to retry; the effect's dep array won't auto-retry on its own.
      return (
        <div style={{ textAlign:'center', padding:'40px 16px', color:'var(--text-dim)', fontSize:13 }}>
          Checking event status…
        </div>
      )
    }
    if (ufcEventState === 'in-calendar-only') {
      // Healthy pre-card state. ESPN has the event on the calendar but
      // hasn't promoted it to events[] with fights yet — typical 1-2
      // weeks before fight night.
      return (
        <div className="alert alert-info" style={{
          margin: '20px 16px', padding: '14px 16px',
          fontSize: 13, lineHeight: 1.5,
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: 6 }}>
            🥊 Picks open closer to fight week
          </div>
          <div style={{ color: 'var(--text-dim)' }}>
            The fight card hasn't been announced yet. Check back soon — UFC typically publishes the full card 1-2 weeks before the event.
          </div>
        </div>
      )
    }
    // 'not-found' → ESPN-confirmed drift. Same warn copy as before the
    // calendar build differentiated the two states.
    return (
      <div className="alert alert-warn" style={{
        margin: '20px 16px', padding: '14px 16px',
        fontSize: 13, lineHeight: 1.5,
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: 6 }}>
          ⚠️ This pool's event isn't on the schedule right now
        </div>
        <div style={{ color: 'var(--text-dim)' }}>
          It may not be published yet (UFC sometimes adds cards close to fight week), or the card changed since this pool was created. Picks will become available once ESPN publishes the event again.
        </div>
      </div>
    )
  }

  if (!isPlayoffMode && games.length === 0) return (
    <div style={{ textAlign:'center', padding:'40px 16px' }}>
      <div style={{ fontSize:'40px', marginBottom:'14px' }}>📅</div>
      <div style={{ fontSize:'17px', fontWeight:'bold', marginBottom:'8px' }}>No {group.sport} games today</div>
      <div style={{ fontSize:'13px', color:'var(--text-dim)' }}>
        {group.sport === 'NFL' ? 'NFL season begins September 5, 2026' : `Check back tomorrow for the next ${group.sport} slate`}
      </div>
    </div>
  )

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      {isPlayoffMode
        ? <PlayoffUI group={group} user={user} games={playoffGames.length > 0 ? playoffGames : games} picks={picks} roundSubmitted={roundSubmitted} onPick={handlePick} onSubmit={handleSubmit} />
        : <DailyUI   group={group} user={user} games={games} picks={picks} submitted={submitted} onPick={handlePick} onSubmit={handleSubmit} />}
      {toast && (
        <div style={{
          position:'fixed', bottom:'90px', left:'50%', transform:'translateX(-50%)',
          background:'var(--surface2)', color:'var(--text)',
          border:'1px solid rgba(0,230,118,0.4)', borderRadius:'8px',
          padding:'8px 16px', fontSize:'13px', fontFamily:'Arial,sans-serif',
          boxShadow:'0 4px 12px rgba(0,0,0,0.4)', zIndex:1000,
          animation:'fadeIn .15s ease-out',
        }}>
          {toast}
        </div>
      )}

      {/* Pattern #2 sticky unlock bar — mirrors GolfDivisionsMode 66f2a6b
          and GroupChat 52dd228 exactly (fixed position, root-context z:90
          above .bottom-nav z:50 and below modals z:100+, gold border, two-
          line label + Unlock button). The in-flow "Lock In" submit button
          inside DailyUI/PlayoffUI stays visible — its onClick calls
          handleSubmit which intercepts to onUnlockRequest. */}
      {locked && (
        <div
          style={{
            position: 'fixed',
            bottom: 80,
            left: 16,
            right: 16,
            maxWidth: 500,
            margin: '0 auto',
            background: 'var(--surface2)',
            border: '1px solid var(--gold)',
            borderRadius: 12,
            padding: '12px 14px',
            zIndex: 90,
            boxShadow: '0 4px 14px rgba(0,0,0,0.4)',
            fontFamily: 'inherit',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
              🔒 Pool is locked
            </div>
            <div style={{
              fontSize: 11, color: 'var(--text-dim)', marginTop: 2,
              fontFamily: 'Arial, sans-serif',
            }}>
              Unlock to make picks
            </div>
          </div>
          <button
            type="button"
            onClick={onUnlockRequest}
            disabled={unlocking}
            className="btn btn-gold"
            style={{
              whiteSpace: 'nowrap',
              padding: '8px 14px',
              fontSize: 13,
              fontWeight: 700,
              opacity: unlocking ? 0.6 : 1,
              cursor: unlocking ? 'wait' : 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {unlocking
              ? 'Unlocking…'
              : (unlockPriceCents != null
                  ? `Unlock $${(unlockPriceCents / 100).toFixed(2)}`
                  : 'Unlock')}
          </button>
        </div>
      )}
    </>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// DAILY / WEEKLY PICK'EM UI
// ═══════════════════════════════════════════════════════════════════════════════

function UFCEventHeader({ game }) {
  if (!game?.eventName) return null
  const dateStr = game.startTimeMs
    ? new Date(game.startTimeMs).toLocaleDateString('en-US', { weekday:'long', month:'short', day:'numeric' })
    : ''
  const timeStr = game.startTimeMs
    ? new Date(game.startTimeMs).toLocaleTimeString('en-US', { hour:'numeric', minute:'2-digit', timeZoneName:'short' })
    : ''
  const meta = [dateStr && timeStr ? `${dateStr} · ${timeStr}` : dateStr || '', game.venue].filter(Boolean).join(' · ')
  return (
    <div style={{
      background:'var(--gold-bg)', border:'1px solid rgba(0,230,118,0.3)',
      borderRadius:8, padding:'12px 16px', marginBottom:12,
    }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, flexWrap:'wrap' }}>
        <span style={{ fontSize:14, fontWeight:'bold', color:'var(--gold)', fontFamily:'inherit' }}>
          🥊 {game.eventName}
        </span>
        {game.isPPV ? (
          <span style={{ background:'linear-gradient(135deg, #00c853, #00e676)', color:'#000', padding:'2px 8px', borderRadius:4, fontSize:11, fontWeight:700, fontFamily:'Arial,sans-serif', letterSpacing:'0.5px' }}>PPV</span>
        ) : (
          <span style={{ background:'#1a1a2a', color:'#aaa', padding:'2px 8px', borderRadius:4, fontSize:11, border:'1px solid #333', fontFamily:'Arial,sans-serif' }}>Fight Night</span>
        )}
      </div>
      {meta && (
        <div style={{ fontSize:11, color:'var(--text-dim)', fontFamily:'Arial,sans-serif' }}>{meta}</div>
      )}
    </div>
  )
}

function DailyUI({ group, games, picks, submitted, onPick, onSubmit }) {
  const isUFC = group.sport === 'UFC'
  // Main-Card-Only pool? When true:
  //   • visibleGames is filtered by isMainCardEligible (fight-ID membership
  //     in MAIN_CARD_COMP_IDS), NOT by round_points (the round_points-based
  //     filter is broken on Freedom 250 per ALL_MAIN_CARD_EVENT_IDS).
  //   • ufcFilter is forced 'main'; the All/Main toggle is hidden.
  //   • If the event's main-card list isn't curated yet
  //     (hasMainCardDefinition === false), the picker surfaces an explicit
  //     "main card not yet defined for this event" panel and skips the
  //     pickable/locked split entirely. Fail closed.
  const isMainCardOnly = isUFC && group.contest_type === 'pick_em_main_card_only'
  const mcoEventId = isMainCardOnly ? group.ufc_config?.eventId : null
  const mcoHasDefinition = isMainCardOnly && hasMainCardDefinition(mcoEventId)
  // UFC-only filter: 'all' shows every fight; 'main' keeps co-main + main +
  // any 5-rounders (anything worth 2+ pts). Tab UI below the event header.
  // MCO pools force 'main' (state ignored, toggle hidden — see below).
  const [ufcFilter, setUfcFilter] = useState('all')
  // UFC-only: which point tiers are collapsed in the picks list. Keyed by
  // roundPoints value (1/2/3/5/7) so toggling persists across the All/Main
  // filter — collapsing Prelims while in "All" then switching to "Main"
  // hides the section either way; switching back keeps it collapsed.
  const [collapsedTiers, setCollapsedTiers] = useState(() => new Set())
  const toggleTier = pts => setCollapsedTiers(prev => {
    const next = new Set(prev)
    if (next.has(pts)) next.delete(pts); else next.add(pts)
    return next
  })
  const mainCardCount = isUFC
    ? games.filter(g => (g.roundPoints ?? g.roundPts ?? 1) >= 2).length
    : 0
  const visibleGames =
    isMainCardOnly
      ? games.filter(g => isMainCardEligible(mcoEventId, g.id))
      : isUFC && ufcFilter === 'main'
        ? games.filter(g => (g.roundPoints ?? g.roundPts ?? 1) >= 2)
        : games

  const pickable    = visibleGames.filter(g => !isGameLocked(g))
  const locked      = visibleGames.filter(g => isGameLocked(g))
  const pickedCount = pickable.filter(g => picks[g.id]).length

  // UFC-only: group pickable fights by point tier for the collapsible
  // section UI. `pickable` is already sorted points-desc (getUFCFights sort),
  // so Map insertion order yields Title → Main Event → Co-Main → Main Card →
  // Prelims naturally. Tiers with zero fights produce no section. Single-tier
  // bypass below renders flat (today's behavior) — wrapping a lone tier in a
  // collapsible would be pointless and would change UX for non-UFC sports.
  const pickableSections = isUFC
    ? (() => {
        const map = new Map()
        for (const g of pickable) {
          const pts = g.roundPoints ?? g.roundPts ?? 1
          if (!map.has(pts)) map.set(pts, [])
          map.get(pts).push(g)
        }
        return [...map.entries()].map(([pts, fights]) => ({ pts, fights }))
      })()
    : []
  const useSections = isUFC && pickableSections.length >= 2

  // Submit threshold: UFC requires every visible fight (All vs Main mode
  // is enforced via visibleGames already). Other sports require at least
  // 10 picks total — counting locked-with-picks too, since a pick made
  // before the lock window still counts toward the user's slate.
  const minPicks = isUFC ? visibleGames.length : 10
  const pickedLocked = locked.filter(g => picks[g.id]).length
  const totalPicked  = pickedCount + pickedLocked
  const canSubmit    = !submitted && totalPicked >= minPicks && minPicks > 0

  // Counts for summary
  const correct  = countByStatus(picks, 'correct')
  const pending  = countByStatus(picks, 'pending')
  const wrong    = countByStatus(picks, 'incorrect')

  // Loud UFC fallback banner: when the group has no ufc_config.eventId
  // (legacy pool, ESPN-was-down-at-create, or pre-Phase-4 schema), the
  // picker falls back to next-event via getUFCFights(undefined). Without
  // this banner the user can't tell the difference between "picks against
  // the configured event" and "picks against whatever ESPN's next event
  // is" — and a mismatch would only surface as grade-time orphans. Pulls
  // the event name from games[0].eventName — verified at odds.js:979
  // (`eventName: event.name || ''`). The `|| 'TBA'` below catches the
  // rare malformed-ESPN-response case where event.name is missing; in
  // that case "TBA" is the intentional loud-signal rather than a clean-
  // looking banner that hides the upstream gap.
  const ufcFallback = isUFC && !group.ufc_config?.eventId && games.length > 0
  const ufcFallbackEventName = ufcFallback ? games[0]?.eventName : null

  return (
    <div>
      {ufcFallback && (
        <div className="alert alert-info" style={{ marginBottom: 12, fontSize: 12, lineHeight: 1.5 }}>
          🥊 Showing next event: <strong>{ufcFallbackEventName || 'TBA'}</strong>
          {' · '}This pool wasn't tied to a specific card at create time, so picks will grade against this event. To pick a different event, create a new pool.
        </div>
      )}
      {isUFC && games.length > 0 && <UFCEventHeader game={games[0]} />}

      {/* Main-Card-Only "not yet defined" panel: when the pool is MCO but
          MAIN_CARD_COMP_IDS has no entry for this event (or the entry is
          empty), the picker can't surface eligible fights. Fail closed
          with an explicit message instead of rendering an empty list.
          Rendered as a sibling of the All/Main toggle so the event header
          above still shows the user which event their pool is for. */}
      {isMainCardOnly && !mcoHasDefinition && (
        <div className="alert alert-error" style={{ marginBottom: 12, fontSize: 13, lineHeight: 1.5 }}>
          🛡️ Main card not yet defined for this event. A Main-Card-Only pool
          needs an operator-curated list of main-card fights before picks
          can be made; that list is pending. Try a regular UFC pick'em pool
          for this event, or check back closer to fight night.
        </div>
      )}

      {isUFC && games.length > 0 && !isMainCardOnly && (
        <div style={{ display:'flex', gap:6, marginBottom:12 }}>
          {[
            { id:'all',  label:'All Fights',  count:games.length },
            { id:'main', label:'Main Card',   count:mainCardCount },
          ].map(opt => {
            const active = ufcFilter === opt.id
            return (
              <button key={opt.id} type="button" onClick={() => setUfcFilter(opt.id)}
                style={{
                  flex:1,
                  background: active ? 'var(--gold-bg2)' : 'var(--surface2)',
                  border: `1px solid ${active ? 'var(--gold)' : 'var(--border)'}`,
                  borderRadius:8, padding:'8px 10px', cursor:'pointer',
                  fontSize:12, fontFamily:'Arial,sans-serif',
                  color: active ? 'var(--gold)' : 'var(--text-dim)',
                  fontWeight: active ? 'bold' : 'normal',
                }}>
                {opt.label} ({opt.count})
              </button>
            )
          })}
        </div>
      )}

      {isUFC && games.length > 0 && <UFCPointLegend />}

      {/* Progress bar (only before submission) */}
      {!submitted && pickable.length > 0 && (
        <DailyProgressBar picked={pickedCount} total={pickable.length} />
      )}

      {/* Post-submit summary */}
      {submitted && (
        <StatusSummary correct={correct} pending={pending} wrong={wrong} />
      )}

      {/* Pickable games — editable until the per-game lock window.
          UFC path groups by point tier into collapsible sections when 2+
          tiers are present (the common case); single-tier and non-UFC
          render flat — identical GameCard props in both branches so the
          picks save/load round-trip is unchanged. */}
      {pickable.length > 0 && (
        <>
          <SectionLabel text="PICK A WINNER" />
          {useSections ? (
            pickableSections.map(({ pts, fights }) => {
              const collapsed    = collapsedTiers.has(pts)
              const pickedInTier = fights.filter(g => picks[g.id]).length
              const total        = fights.length
              const complete     = total > 0 && pickedInTier === total
              const tierLabel    = UFC_TIER_LABELS[pts] || `${pts}pt`
              return (
                <div key={pts} style={{ marginBottom: 4 }}>
                  <button
                    type="button"
                    onClick={() => toggleTier(pts)}
                    aria-expanded={!collapsed}
                    style={{
                      display:'flex', width:'100%',
                      alignItems:'center', justifyContent:'space-between', gap:12,
                      padding:'10px 14px', marginBottom:8,
                      background:'var(--surface2)', border:'1px solid var(--border)',
                      borderRadius:8, cursor:'pointer',
                      fontFamily:'Arial,sans-serif',
                    }}
                  >
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <span style={{
                        fontSize:10, padding:'3px 7px', borderRadius:3,
                        fontWeight:'bold',
                        ...ufcPtBadgeStyle(pts),
                      }}>
                        {ptsLabel(pts)}
                      </span>
                      <span style={{ fontSize:13, fontWeight:'bold', color:'var(--text-dim)' }}>
                        {tierLabel}
                      </span>
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <span style={{
                        fontSize:12,
                        color: complete ? 'var(--green)' : 'var(--text-muted)',
                        fontWeight: complete ? 'bold' : 'normal',
                      }}>
                        {complete ? `✓ ${total}/${total}` : `${pickedInTier}/${total} picked`}
                      </span>
                      <span style={{ fontSize:10, color:'var(--text-muted)' }}>
                        {collapsed ? '▼' : '▲'}
                      </span>
                    </div>
                  </button>
                  {!collapsed && fights.map(game => (
                    <GameCard key={game.id} game={game} pick={picks[game.id]}
                      readOnly={false} onPick={side => onPick(game.id, side, null)} />
                  ))}
                </div>
              )
            })
          ) : (
            pickable.map(game => (
              <GameCard key={game.id} game={game} pick={picks[game.id]}
                readOnly={false} onPick={side => onPick(game.id, side, null)} />
            ))
          )}
        </>
      )}

      {/* Locked/live/final games */}
      {locked.length > 0 && (
        <>
          <SectionLabel text="LOCKED" icon="🔒" />
          {locked.map(game => (
            <GameCard key={game.id} game={game} pick={picks[game.id]}
              readOnly={true} onPick={() => {}} />
          ))}
        </>
      )}

      {/* Status banner — reflects the three picks-state cases */}
      <div style={{ marginTop:'16px' }}>
        {(() => {
          const totalPicked = Object.values(picks).filter(Boolean).length
          if (totalPicked === 0) {
            return (
              <div style={{ textAlign:'center', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:'8px', padding:'10px 16px', color:'var(--text-dim)', fontSize:'13px' }}>
                ⏰ Make your picks before games start
              </div>
            )
          }
          if (pickable.length === 0) {
            return (
              <div style={{ textAlign:'center', background:'rgba(46,204,113,0.10)', border:'1px solid rgba(46,204,113,0.35)', borderRadius:'8px', padding:'10px 16px', color:'var(--green)', fontSize:'13px', fontWeight:'bold' }}>
                ✅ Picks Locked In
              </div>
            )
          }
          return (
            <div style={{ textAlign:'center', background:'var(--gold-bg)', border:'1px solid rgba(0,230,118,0.3)', borderRadius:'8px', padding:'10px 16px', color:'var(--gold)', fontSize:'13px' }}>
              ✏️ Picks saved · Some games still open
            </div>
          )
        })()}

        {/* Submit button — explicit "I'm done" gesture; credits total_picks.
            Always rendered when there are pickable games and not yet
            submitted; disabled state carries the threshold messaging. */}
        {pickable.length > 0 && !submitted && (
          <>
            <button
              className="btn btn-gold"
              onClick={() => onSubmit(null)}
              disabled={!canSubmit}
              style={{ marginTop:'12px', opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? 'pointer' : 'not-allowed' }}
            >
              {canSubmit
                ? `Lock In ${totalPicked} Pick${totalPicked !== 1 ? 's' : ''}`
                : isUFC
                  ? `Pick all fights to submit (${totalPicked}/${minPicks})`
                  : `Pick at least ${minPicks} games to submit (${totalPicked}/${minPicks})`}
            </button>
            {!canSubmit && (
              <div style={{ textAlign:'center', fontSize:'11px', color:'var(--text-muted)', marginTop:'6px' }}>
                {totalPicked} / {minPicks} picks made
              </div>
            )}
          </>
        )}

        <div style={{ textAlign:'center', fontSize:'11px', color:'var(--text-muted)', marginTop:'10px' }}>
          Picks lock 10 min before each game
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// PLAYOFF PICK'EM UI
// ═══════════════════════════════════════════════════════════════════════════════

function PlayoffUI({ group, games, picks, roundSubmitted, onPick, onSubmit }) {
  const rounds      = PLAYOFF_ROUND_INFO[group.sport] || []
  const [activeRound, setActiveRound] = useState(null)

  useEffect(() => {
    getActiveRound(group.sport).then(setActiveRound)
  }, [group.sport])

  const activeInfo  = rounds.find(r => r.round === activeRound) || null

  // Cumulative stats across all rounds
  const correct   = countByStatus(picks, 'correct')
  const pending   = countByStatus(picks, 'pending')
  const wrong     = countByStatus(picks, 'incorrect')
  const totalPicks = correct + pending + wrong
  const pts       = Object.values(picks).reduce((s, p) => s + (p?.pointsAwarded || 0), 0)

  // Points available today: sum of (roundPts) for unlocked games starting today
  const todayKey = new Date().toDateString()
  const todayUnlockedGames = games.filter(g =>
    !isGameLocked(g) && g.startTimeMs && new Date(g.startTimeMs).toDateString() === todayKey
  )
  const pointsAvailableToday = todayUnlockedGames.reduce((s, g) => s + (g.roundPts || 1), 0)

  return (
    <div>
      {/* Playoff scoring guide */}
      <div style={{ background:'linear-gradient(135deg, var(--gold-bg) 0%, var(--gold-bg2) 100%)', border:'1px solid rgba(0,230,118,0.35)', borderRadius:'var(--radius)', padding:'12px 16px', marginBottom:'12px', textAlign:'center' }}>
        <div style={{ fontSize:'16px', fontWeight:'bold', color:'var(--gold)', marginBottom:'4px' }}>🏆 Playoffs Pick'em</div>
        <div style={{ fontSize:'11px', color:'var(--text-dim)', fontFamily:'Arial,sans-serif', letterSpacing:'0.3px' }}>
          Round 1: 1pt · Round 2: 2pts · Conf Finals: 4pts · Finals: 8pts
        </div>
      </div>

      {/* Playoff summary banner */}
      <div style={{ background:'var(--gold-bg)', border:'1px solid rgba(0,230,118,0.25)', borderRadius:'var(--radius)', padding:'14px 16px', marginBottom:'16px' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'12px' }}>
          <div>
            <div style={{ fontSize:'11px', color:'var(--text-dim)', marginBottom:'4px', fontFamily:'Arial,sans-serif', letterSpacing:'0.5px' }}>TOTAL POINTS</div>
            <div style={{ fontSize:'30px', fontWeight:'bold', color:'var(--gold)', fontFamily:'Arial,sans-serif', lineHeight:1 }}>{pts}</div>
            {activeInfo && (
              <div style={{ fontSize:'11px', color:'var(--text-dim)', marginTop:'8px', fontFamily:'Arial,sans-serif' }}>
                Now: <strong style={{ color:'var(--gold)' }}>{activeInfo.name}</strong> · {activeInfo.pts} pt{activeInfo.pts !== 1 ? 's' : ''}/pick
              </div>
            )}
          </div>
          <div style={{ textAlign:'right', fontSize:'12px', color:'var(--text-dim)', lineHeight:'1.7' }}>
            <div>✅ {correct} / {totalPicks || '0'} correct</div>
            <div>⏳ {pending} pending</div>
            {pointsAvailableToday > 0 && (
              <div style={{ marginTop:'4px', color:'var(--gold)', fontWeight:'bold', fontFamily:'Arial,sans-serif' }}>
                {pointsAvailableToday} pt{pointsAvailableToday !== 1 ? 's' : ''} available today
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Each round section */}
      {rounds.map(round => {
        const roundGames  = games.filter(g => g.round === round.round)
        const isActive    = round.round === activeRound
        const isPast      = activeRound !== null && round.round < activeRound
        const isFuture    = activeRound !== null && round.round > activeRound
        const isSubmitted = !!roundSubmitted[round.id]
        const pickable    = roundGames.filter(g => !isGameLocked(g))
        const pickedCount = pickable.filter(g => picks[g.id]).length
        const canSubmit   = !isSubmitted && pickedCount > 0 && pickedCount === pickable.length

        return (
          <div key={round.id} style={{ marginBottom:'20px' }}>
            {/* Round header */}
            <div style={{
              display:'flex', alignItems:'center', justifyContent:'space-between',
              padding:'10px 14px',
              background: isActive ? 'var(--gold-bg)' : isFuture ? 'var(--surface2)' : 'var(--surface2)',
              border: `1px solid ${isActive ? 'rgba(0,230,118,0.35)' : 'var(--border)'}`,
              borderRadius:'10px', marginBottom:'10px',
            }}>
              <div>
                <div style={{ fontSize:'13px', fontWeight:'bold', color: isActive ? 'var(--gold)' : isFuture ? 'var(--text-muted)' : 'var(--text)' }}>
                  {isPast && '🔒 '}{round.name}
                </div>
                <div style={{ fontSize:'11px', color:'var(--text-dim)', marginTop:'2px' }}>
                  {round.pts} pt{round.pts !== 1 ? 's' : ''} per correct pick
                </div>
              </div>
              <div style={{ textAlign:'right' }}>
                {isActive && !isSubmitted && (
                  <div style={{ fontSize:'12px', color:'var(--gold)' }}>
                    {pickedCount}/{pickable.length} picked
                  </div>
                )}
                {isSubmitted && (
                  <div style={{ fontSize:'12px', color:'var(--green)' }}>✅ Submitted</div>
                )}
                {isFuture && (
                  <div style={{ fontSize:'11px', color:'var(--text-muted)' }}>~{round.estimatedEnd}</div>
                )}
              </div>
            </div>

            {/* Future round — locked */}
            {isFuture && (
              <div style={{ textAlign:'center', padding:'20px', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:'var(--radius)', color:'var(--text-muted)', fontSize:'13px' }}>
                🔒 Unlocks when {rounds[round.round - 2]?.name || 'previous round'} completes
                <div style={{ fontSize:'11px', marginTop:'4px' }}>Est. ~{round.estimatedEnd}</div>
              </div>
            )}

            {/* No games for this round yet */}
            {!isFuture && roundGames.length === 0 && (
              <div style={{ textAlign:'center', padding:'20px', color:'var(--text-muted)', fontSize:'13px' }}>
                Matchups TBD — determined by Round {round.round - 1} results
              </div>
            )}

            {/* Round games */}
            {!isFuture && roundGames.length > 0 && (
              <>
                {roundGames.map(game => (
                  <GameCard key={game.id} game={game} pick={picks[game.id]}
                    readOnly={false}
                    onPick={side => onPick(game.id, side, round.id)} />
                ))}

                {/* Submit button for this round */}
                {isActive && !isSubmitted && (
                  <button
                    className="btn btn-gold btn-sm"
                    style={{ marginTop:'8px' }}
                    onClick={() => onSubmit(round.id)}
                    disabled={!canSubmit}
                  >
                    {canSubmit
                      ? `Submit Round ${round.round} (${pickedCount} pick${pickedCount !== 1 ? 's' : ''})`
                      : pickable.length === 0 ? 'No pickable games right now' : `Pick all ${pickable.length} games to submit`}
                  </button>
                )}
                {isSubmitted && (
                  <div className="btn btn-gold btn-sm" style={{ marginTop:'8px', opacity:0.5, cursor:'default', textAlign:'center' }}>
                    ✅ Round {round.round} Picks Locked
                  </div>
                )}
              </>
            )}
          </div>
        )
      })}

      <div style={{ textAlign:'center', fontSize:'11px', color:'var(--text-muted)', marginTop:'8px' }}>
        Points awarded when each game goes final · Leaderboard updates live
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// SHARED SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

function DailyProgressBar({ picked, total }) {
  const pct = total === 0 ? 0 : Math.round((picked / total) * 100)
  return (
    <div style={{ marginBottom:'18px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'6px' }}>
        <span style={{ fontSize:'13px', color:'var(--text-dim)' }}>Picks made</span>
        <span style={{ fontSize:'13px', fontWeight:'bold', color: picked === total && total > 0 ? 'var(--green)' : 'var(--gold)' }}>
          {picked} / {total}
        </span>
      </div>
      <div className="progress-bar">
        <div className="progress-bar-fill" style={{
          width:`${pct}%`,
          background: picked === total && total > 0 ? 'linear-gradient(90deg,#2ecc71,#27ae60)' : undefined,
        }} />
      </div>
    </div>
  )
}

function StatusSummary({ correct, pending, wrong }) {
  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'8px', marginBottom:'16px' }}>
      {[
        { icon:'✅', label:'Correct',   val:correct, color:'var(--green)' },
        { icon:'⏳', label:'Pending',   val:pending, color:'var(--gold)'  },
        { icon:'❌', label:'Incorrect', val:wrong,   color:'var(--red)'   },
      ].map(s => (
        <div key={s.label} style={{ background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:'10px', padding:'10px 8px', textAlign:'center' }}>
          <div style={{ fontSize:'18px', marginBottom:'3px' }}>{s.icon}</div>
          <div style={{ fontSize:'18px', fontWeight:'bold', color:s.color, fontFamily:'Arial,sans-serif' }}>{s.val}</div>
          <div style={{ fontSize:'11px', color:'var(--text-dim)' }}>{s.label}</div>
        </div>
      ))}
    </div>
  )
}

function SectionLabel({ text, icon }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'11px', fontFamily:'Arial,sans-serif', color:'var(--text-dim)', letterSpacing:'0.8px', marginBottom:'8px', marginTop:'4px' }}>
      {icon && <span>{icon}</span>}
      {text}
    </div>
  )
}

// ── Game card ─────────────────────────────────────────────────────────────────

function GameCard({ game, pick, readOnly, onPick }) {
  const [expanded, setExpanded] = useState(false)
  // Surfaces the athlete-id plumbing the moment a UFC card is expanded so
  // a "no stats" result can be diagnosed against the value here.
  useEffect(() => {
    if (expanded && game.sport === 'UFC') {
      log('[PickemMode] UFC compare:', {
        home:          game.home?.team,
        away:          game.away?.team,
        homeAthleteId: game.homeAthleteId,
        awayAthleteId: game.awayAthleteId,
      })
    }
  }, [expanded, game.sport, game.homeAthleteId, game.awayAthleteId])
  const isLive   = game.status === 'live'
  const isFinal  = game.status === 'final'
  const isUpcoming = game.status === 'upcoming'
  const locked   = readOnly || isGameLocked(game)

  const pickStatus = pick?.status
  const pickedSide = pick?.side
  const pts        = pick?.pointsAwarded

  const borderColor = pickStatus === 'correct' ? 'rgba(46,204,113,0.55)'
                    : pickStatus === 'incorrect' ? 'rgba(255,59,59,0.45)'
                    : pickedSide ? 'rgba(0,230,118,0.2)' : 'var(--border)'

  // Final-game outcome banner (rendered just below the header). Only shown
  // when the game is final AND the user actually has a pick on it.
  const winnerSide = isFinal && game.away.score != null && game.home.score != null
    ? (game.away.score > game.home.score ? 'away' : game.home.score > game.away.score ? 'home' : null)
    : null
  const winnerTeam = winnerSide === 'away' ? game.away.team : winnerSide === 'home' ? game.home.team : null
  const resultBanner = isFinal && pickedSide ? (
    pickStatus === 'correct'   ? { text: `✓ CORRECT — +${pts ?? 1}pt`,        bg: 'rgba(46,204,113,0.18)', color: 'var(--green)' } :
    pickStatus === 'incorrect' ? { text: `✗ WRONG${winnerTeam ? ` — Winner: ${winnerTeam}` : ''}`, bg: 'rgba(255,59,59,0.14)', color: 'var(--red)' } :
                                 { text: '⏳ Pending grade',                  bg: 'var(--gold-bg)',         color: 'var(--gold)'  }
  ) : null

  // Fallbacks for upstream odds that may live under game.odds.* depending on
  // where the game came from (getGames merge vs. getPlayoffGames adapter).
  const spread = game.spread ?? game.odds?.spread ?? null
  const awayML = game.awayML ?? game.odds?.awayML ?? null
  const homeML = game.homeML ?? game.odds?.homeML ?? null
  const total  = game.total  ?? game.odds?.total  ?? null

  const awayIsFav = awayML != null && awayML < 0
  const homeIsFav = homeML != null && homeML < 0
  // Compute formatted strings — fmtML/spreadLabel return '' for null, treat '' as missing
  const awaySpread = spreadLabel(spread, 'away')
  const homeSpread = spreadLabel(spread, 'home')
  const awayMLStr  = fmtML(awayML)
  const homeMLStr  = fmtML(homeML)

  // UFC gets its own hero treatment — fighters not teams, corner colors
  // not team colors, weight-class header, card-position points. Parallel
  // to HeroCard rather than absorbed into HERO_SPORTS because the
  // structural differences are too deep for one template. UFC never sets
  // isPlayoff (parseESPN team-sport path only), so no playoff gate needed.
  if (game.sport === 'UFC' && isUpcoming) {
    return (
      <UFCHeroCard
        game={game}
        pickedSide={pickedSide}
        pickStatus={pickStatus}
        locked={locked}
        onPick={onPick}
        expanded={expanded}
        setExpanded={setExpanded}
      />
    )
  }

  // Regular-season upcoming team-sport games in HERO_SPORTS (MLB, NBA, NHL)
  // render the team-color-driven hero treatment. Playoff games keep the
  // existing card below (round caption / series info chrome the new layout
  // doesn't accommodate); NFL falls through (hero not built); UFC has its
  // own UFCHeroCard dispatched above this one.
  if (HERO_SPORTS.has(game.sport) && isUpcoming && !game.isPlayoff) {
    return (
      <HeroCard
        game={game}
        pickedSide={pickedSide}
        pickStatus={pickStatus}
        locked={locked}
        onPick={onPick}
        expanded={expanded}
        setExpanded={setExpanded}
        total={total}
        awaySpread={awaySpread}
        homeSpread={homeSpread}
        awayMLStr={awayMLStr}
        homeMLStr={homeMLStr}
        awayIsFav={awayIsFav}
        homeIsFav={homeIsFav}
      />
    )
  }

  return (
    <div style={{ background:'var(--surface)', border:`1px solid ${borderColor}`, borderRadius:'var(--radius)', padding:'14px', marginBottom:'10px', opacity: isFinal && !pickedSide ? 0.65 : 1 }}>
      {/* Playoff round caption — small gold caps at top of card */}
      {game.isPlayoff && game.roundName && (
        <div style={{ fontSize:'10px', fontWeight:'bold', color:'var(--gold)', letterSpacing:'1.5px', textTransform:'uppercase', marginBottom:'8px', fontFamily:'Arial,sans-serif' }}>
          {game.roundName}
        </div>
      )}

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
          {isLive  && <span className="badge badge-live">LIVE</span>}
          {isFinal && <span className="badge badge-final">FINAL</span>}
          {!isLive && !isFinal && locked && (
            <span style={{ fontSize:'10px', background:'var(--gold-bg)', color:'var(--gold)', padding:'3px 8px', borderRadius:'4px', fontWeight:'bold', fontFamily:'Arial,sans-serif', letterSpacing:'0.5px' }}>
              🔒 LOCKED
            </span>
          )}
          {!isLive && !isFinal && !locked && (
            <div style={{ display:'flex', flexDirection:'column', lineHeight:1.2 }}>
              <span style={{ fontSize:'13px', color:'var(--gold)' }}>{game.startTime}</span>
              {game.startTimeMs && <Countdown commenceTime={game.startTimeMs} />}
            </div>
          )}
          {isLive && game.period && (
            <span style={{ fontSize:'12px', color:'var(--text-dim)', fontFamily:'Arial,sans-serif' }}>{game.period} {game.clock}</span>
          )}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'6px', minWidth:0, flexWrap:'wrap', justifyContent:'flex-end' }}>
          {!game.isPlayoff && game.roundName && <span style={{ fontSize:'10px', background:'var(--surface2)', color:'var(--text-dim)', padding:'2px 6px', borderRadius:'4px', flexShrink:0 }}>{game.roundName}</span>}
          {(game.roundPts > 1 || game.sport === 'UFC') && (
            <span style={{ fontSize:'10px', padding:'2px 6px', borderRadius:'4px', fontWeight:'bold', fontFamily:'Arial,sans-serif', flexShrink:0, ...(game.sport === 'UFC' ? ufcPtBadgeStyle(game.roundPts) : roundPtBadgeStyle(game.roundPts)) }}>
              {game.roundPts} PT{game.roundPts !== 1 ? 'S' : ''}
            </span>
          )}
          {game.gameNumber != null && (
            <span style={{ fontSize:'11px', color:'var(--text-dim)', flexShrink:0, fontFamily:'Arial,sans-serif' }}>Game {game.gameNumber}</span>
          )}
          {!game.isPlayoff && game.seriesInfo && <span style={{ fontSize:'11px', color:'var(--text-dim)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{game.seriesInfo}</span>}
        </div>
      </div>

      {/* Final-game pick result banner */}
      {resultBanner && (
        <div style={{
          background: resultBanner.bg, color: resultBanner.color,
          fontFamily: 'Arial,sans-serif', fontWeight: 'bold', fontSize: 12, letterSpacing: '0.4px',
          padding: '8px 10px', borderRadius: 8, marginBottom: 12, textAlign: 'center',
        }}>
          {resultBanner.text}
        </div>
      )}

      {/* Team buttons — simplified, no odds inside */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 28px 1fr', gap:'8px', alignItems:'center' }}>
        <TeamButton teamObj={game.away} sport={game.sport} side="away" pickedSide={pickedSide} pickStatus={pickStatus} locked={locked} isLive={isLive} isFinal={isFinal} pts={pts} onClick={() => onPick('away')} />
        <div style={{ textAlign:'center', fontSize:'12px', color:'var(--text-muted)', fontWeight:'bold' }}>@</div>
        <TeamButton teamObj={game.home} sport={game.sport} side="home" pickedSide={pickedSide} pickStatus={pickStatus} locked={locked} isLive={isLive} isFinal={isFinal} pts={pts} onClick={() => onPick('home')} />
      </div>

      {/* Playoff series record — below team names */}
      {game.isPlayoff && game.seriesInfo && (
        <div style={{ textAlign:'center', fontSize:'11px', color:'var(--text-dim)', marginTop:'8px', fontFamily:'Arial,sans-serif', letterSpacing:'0.3px' }}>
          {game.seriesInfo}
        </div>
      )}

      {/* ── ODDS STRIP ── always shown for upcoming games */}
      {isUpcoming && (
        <div style={{ borderTop:'1px solid rgba(255,255,255,0.08)', marginTop:'12px', paddingTop:'12px', background:'rgba(255,255,255,0.02)', borderRadius:'0 0 8px 8px', margin:'12px -14px -14px', padding:'12px 14px 14px' }}>
          {/* Row label */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' }}>
            <span style={{ fontSize:'9px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif', letterSpacing:'1.5px', fontWeight:'bold' }}>SPREAD · MONEYLINE</span>
            <span style={{ fontSize:'9px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif', letterSpacing:'1.5px', fontWeight:'bold' }}>O/U</span>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 56px 1fr', gap:'8px', alignItems:'center' }}>
            <OddsTeam abbr={game.away.abbr} spread={awaySpread} ml={awayMLStr} isFav={awayIsFav} isUnderdg={!awayIsFav && !!awayMLStr} align="left" arrow={<LineArrow game={game} side="away" />} />
            {/* Center: O/U */}
            <div style={{ textAlign:'center' }}>
              <div style={{ fontSize:'10px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif', marginBottom:'2px' }}>O/U</div>
              <div style={{ fontSize:'17px', fontWeight:'bold', color: total != null ? 'var(--text)' : 'var(--text-muted)', fontFamily:'Arial,sans-serif', lineHeight:1 }}>
                {total != null ? total : '—'}
              </div>
            </div>
            <OddsTeam abbr={game.home.abbr} spread={homeSpread} ml={homeMLStr} isFav={homeIsFav} isUnderdg={!homeIsFav && !!homeMLStr} align="right" arrow={<LineArrow game={game} side="home" />} />
          </div>
        </div>
      )}

      {/* Toggle */}
      <button
        onClick={e => { e.stopPropagation(); setExpanded(x => !x) }}
        style={{ width:'100%', background:'none', border:'none', borderTop:'1px solid var(--border)', color: expanded ? 'var(--gold)' : 'var(--text-dim)', fontSize:'11px', marginTop:'10px', paddingTop:'8px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'4px', fontFamily:'Arial,sans-serif', letterSpacing:'0.4px' }}
      >
        {(() => {
          const isUFC = game.sport === 'UFC'
          if (expanded) {
            if (isFinal) return '▲ Hide Box Score'
            return isUFC ? '▲ Hide Fighters' : '▲ Hide Stats'
          }
          if (isFinal) return '▼ Box Score'
          return isUFC ? '▼ Compare Fighters' : '▼ Compare Teams'
        })()}
      </button>

      {expanded && (isFinal ? <BoxScore game={game} /> : <TeamComparison game={game} />)}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// HERO CARD — team-color-driven shell for regular-season upcoming games in
// HERO_SPORTS (MLB, NBA, NHL). Wraps the same data contract as GameCard
// (onPick / expand / TeamComparison) in a new visual treatment; falls back
// gracefully when team color data is missing or near-black (pickFill returns
// neutral, glow becomes near-invisible but layout still renders).
//
// NBA and NHL shipped build-green-verified only — visual walk deferred until
// regular-season games exist in the live feed (Finals window has no eligible
// upcoming games to render). See HANDOFF.md for the deferred-verification gate.
// ═══════════════════════════════════════════════════════════════════════════════

// Convert "#rrggbb" → "rgba(r,g,b,a)". Assumes already-normalized hex from
// pickFill (which guarantees the #rrggbb shape or NEUTRAL_BG).
function hexToRGBA(hex, alpha) {
  const h = hex.replace('#', '')
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  return `rgba(${r},${g},${b},${alpha})`
}

function HeroCard({ game, pickedSide, pickStatus, locked, onPick, expanded, setExpanded, total, awaySpread, homeSpread, awayMLStr, homeMLStr, awayIsFav, homeIsFav }) {
  // iconicTeamColor returns a curated brand hue for sport+abbr keys in
  // ICONIC_COLOR_OVERRIDES (e.g. MLB:BOS → Red Sox red, NHL:BOS → Bruins
  // gold, NBA:LAC → Clippers red, NHL:VGK → Vegas gold), and falls through
  // to pickFill for everyone else — so near-black or missing ESPN colors
  // still resolve cleanly through pickFill's primary/alternate/neutral
  // pipeline. pickFill itself is unchanged by the override layer.
  const homeColor = iconicTeamColor(game.sport, game.home.abbr, game.home.color, game.home.alternateColor)
  const awayColor = iconicTeamColor(game.sport, game.away.abbr, game.away.color, game.away.alternateColor)

  // Container border reflects pick state (mirrors existing GameCard).
  const containerBorder =
    pickStatus === 'correct'   ? 'rgba(46,204,113,0.55)' :
    pickStatus === 'incorrect' ? 'rgba(255,59,59,0.45)'  :
    pickedSide                 ? 'rgba(0,230,118,0.20)'  : 'rgba(255,255,255,0.06)'

  return (
    <div style={{
      background: '#0d1016',
      border: `1px solid ${containerBorder}`,
      borderRadius: '18px',
      padding: '22px 22px 14px',
      marginBottom: '10px',
      boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
    }}>
      <HeroLockRow startTime={game.startTime} startTimeMs={game.startTimeMs} locked={locked} />

      {/* Matchup row — app convention: away on the LEFT, home on the RIGHT. */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr auto 1fr', alignItems:'center' }}>
        <TeamHero
          teamObj={game.away}
          side="away"
          color={awayColor}
          picked={pickedSide === 'away'}
          pickStatus={pickStatus}
          locked={locked}
          onClick={() => onPick('away')}
        />
        <div style={{
          width:56, height:56, borderRadius:'50%',
          background:'#0d1016',
          border:'1px solid rgba(255,255,255,0.10)',
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:'15px', fontWeight:800, letterSpacing:'0.05em',
          color:'#aab2bd',
          zIndex:5, margin:'0 -14px',
          fontFamily:'Arial,sans-serif',
        }}>VS</div>
        <TeamHero
          teamObj={game.home}
          side="home"
          color={homeColor}
          picked={pickedSide === 'home'}
          pickStatus={pickStatus}
          locked={locked}
          onClick={() => onPick('home')}
        />
      </div>

      <HeroOdds
        sport={game.sport}
        awayAbbr={game.away.abbr} homeAbbr={game.home.abbr}
        awayColor={awayColor} homeColor={homeColor}
        awaySpread={awaySpread} homeSpread={homeSpread}
        awayMLStr={awayMLStr} homeMLStr={homeMLStr}
        awayIsFav={awayIsFav} homeIsFav={homeIsFav}
        total={total}
      />

      {/* Expand toggle — same state and downstream behavior as GameCard's. */}
      <button
        onClick={e => { e.stopPropagation(); setExpanded(x => !x) }}
        style={{
          width:'100%',
          background:'none', border:'none',
          borderTop:'1px solid rgba(255,255,255,0.06)',
          marginTop:'14px',
          paddingTop:'12px', paddingBottom:'4px',
          color: expanded ? 'var(--gold)' : '#aab2bd',
          fontSize:'13px', fontWeight:600,
          cursor:'pointer',
          display:'flex', alignItems:'center', justifyContent:'center', gap:'6px',
          fontFamily:'Arial,sans-serif', letterSpacing:'0.4px',
        }}
      >
        {expanded ? '▲ Hide Stats' : '▼ Compare Teams'}
      </button>

      {expanded && <TeamComparison game={game} />}
    </div>
  )
}

function HeroLockRow({ startTime, startTimeMs, locked }) {
  // Lock icon ring → gold/amber per UFC mock; locked state stays
  // muted-grey. Bracket pickem reuses HeroLockRow but rare enough
  // that the gold accent reads as "fight starting soon" rather than
  // bracket-specific — confirmed visually OK during the operator review.
  const ringColor = locked ? 'var(--text-muted)' : '#F5B800'
  return (
    <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'18px' }}>
      <div style={{
        width:28, height:28, borderRadius:'50%',
        border:`1.5px solid ${ringColor}`,
        display:'flex', alignItems:'center', justifyContent:'center',
        fontSize:'13px',
        flexShrink:0,
      }}>🔒</div>
      <div style={{ display:'flex', flexDirection:'column', gap:'2px', minWidth:0 }}>
        <div style={{
          fontSize:'18px', fontWeight:800,
          color: locked ? 'var(--text-muted)' : '#F5B800',
          letterSpacing:'0.01em',
          fontFamily:'Arial,sans-serif',
          lineHeight:1,
          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
        }}>
          {locked ? '🔒 LOCKED' : startTime}
        </div>
        {!locked && startTimeMs && (
          <Countdown commenceTime={startTimeMs} style={{ fontSize:'12px', color:'#7a828d', fontWeight:500 }} />
        )}
      </div>
    </div>
  )
}

function TeamHero({ teamObj, side, color, picked, pickStatus, locked, onClick }) {
  const isCorrect = picked && pickStatus === 'correct'
  const isWrong   = picked && pickStatus === 'incorrect'
  const isPending = picked && pickStatus === 'pending'

  // Glow positioning: away (left card) → glow at left-top; home (right
  // card) → glow at right-top. Mirror of the mockup's home/away mapping
  // since this app puts away on the left ("AWAY @ HOME").
  const glowPos       = side === 'away' ? '12% 18%' : '88% 18%'
  const gradientAngle = side === 'away' ? '160deg'  : '200deg'

  const glow  = hexToRGBA(color, 0.55)
  const tint  = hexToRGBA(color, 0.16)
  const inset = hexToRGBA(color, 0.10)

  const borderColor = isCorrect ? 'var(--green)' : isWrong ? 'var(--red)' : color
  const pickedBadge = picked && (
    isCorrect ? { ch: '✓', bg: 'var(--green)' } :
    isWrong   ? { ch: '✗', bg: 'var(--red)'   } :
    isPending ? { ch: '⏳', bg: 'var(--gold)'  } :
                { ch: '✓', bg: 'var(--gold)'  }
  )

  // Abbr fallback mirrors TeamLogo at the top of this file: prefer the
  // ESPN abbreviation, else derive from the last word of the team name.
  const badgeText = (teamObj.abbr && teamObj.abbr.trim())
    || ((teamObj.team || '').split(' ').pop() || '???').slice(0, 3).toUpperCase()

  return (
    <button
      onClick={onClick}
      disabled={locked}
      style={{
        position:'relative',
        borderRadius:'16px',
        padding:'24px 14px 18px',
        overflow:'hidden',
        cursor: locked ? 'default' : 'pointer',
        transition:'transform .12s ease',
        minHeight:'200px',
        width:'100%',
        display:'flex', flexDirection:'column',
        alignItems:'center', justifyContent:'center',
        background:`radial-gradient(120% 90% at ${glowPos}, ${glow} 0%, rgba(0,0,0,0) 55%), linear-gradient(${gradientAngle}, ${tint} 0%, rgba(10,12,16,0.2) 70%), #11151c`,
        border:`1.5px solid ${borderColor}`,
        boxShadow:`inset 0 0 60px ${inset}`,
        opacity: locked && !picked ? 0.7 : 1,
        fontFamily:'Arial,sans-serif',
      }}
    >
      {pickedBadge && (
        <span style={{
          position:'absolute', top:'10px', right:'10px',
          background: pickedBadge.bg,
          color:'#0d1016', borderRadius:'50%',
          width:'22px', height:'22px',
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:'12px', fontWeight:'bold',
          zIndex:3,
        }}>
          {pickedBadge.ch}
        </span>
      )}

      {/* Badge — dark fill, team-color border, abbr text */}
      <div style={{
        width:'82px', height:'82px', borderRadius:'50%',
        display:'flex', alignItems:'center', justifyContent:'center',
        fontSize:'28px', fontWeight:800, letterSpacing:'0.01em',
        background:'rgba(8,10,14,0.55)',
        marginBottom:'12px',
        position:'relative', zIndex:2,
        border:`3px solid ${color}`,
        color:'#f4f6f8',
      }}>
        {badgeText}
      </div>

      {/* Team name + team-color underline */}
      <div style={{ position:'relative', zIndex:2, textAlign:'center' }}>
        <div style={{
          fontSize:'18px', fontWeight:800, textTransform:'uppercase',
          letterSpacing:'0.02em', lineHeight:1.1,
          color:'#f4f6f8',
          wordBreak:'break-word',
        }}>
          {teamObj.team}
        </div>
        <div style={{
          width:'40px', height:'3px', borderRadius:'2px',
          margin:'8px auto 0',
          background: color,
        }}/>
      </div>

      {teamObj.record && (
        <div style={{
          fontSize:'13px', color:'#7a828d',
          marginTop:'10px',
          position:'relative', zIndex:2,
        }}>
          {teamObj.record}
        </div>
      )}

      {picked && !locked && (
        <div style={{
          position:'absolute', bottom:'8px', right:'10px',
          fontSize:'9px', color:'#7a828d',
          letterSpacing:'0.3px',
          zIndex:2,
        }}>
          ✏️ tap to change
        </div>
      )}
    </button>
  )
}

function HeroOdds({ sport, awayAbbr, homeAbbr, awayColor, homeColor, awaySpread, homeSpread, awayMLStr, homeMLStr, awayIsFav, homeIsFav, total }) {
  // Per Phase 0: fillMissingOdds synthesizes spread/ML so these are
  // effectively never null at render. Only `total` (O/U) is reliably
  // null without an Odds API key — that's the column-specific missing
  // state we surface.
  const ouAvailable = total != null
  // NHL convention is "PUCK LINE"; other sports get "SPREAD".
  const spreadLabel = SPREAD_LABEL_BY_SPORT[sport] || 'SPREAD'

  return (
    <div style={{
      marginTop:'16px',
      background:'#0b0e13',
      border:'1px solid rgba(255,255,255,0.06)',
      borderRadius:'14px',
      display:'grid',
      gridTemplateColumns:'1fr 1fr 1fr',
    }}>
      <OddsCol label={spreadLabel}>
        <OddsLine tm={awayAbbr} tmColor={awayColor} val={awaySpread || '—'} valBold={awayIsFav} />
        <OddsLine tm={homeAbbr} tmColor={homeColor} val={homeSpread || '—'} valBold={homeIsFav} />
      </OddsCol>
      <OddsCol label="MONEYLINE">
        <OddsLine tm={awayAbbr} tmColor={awayColor} val={awayMLStr || '—'} valBold={awayIsFav} />
        <OddsLine tm={homeAbbr} tmColor={homeColor} val={homeMLStr || '—'} valBold={homeIsFav} />
      </OddsCol>
      <OddsCol label="O/U" isLast>
        {ouAvailable ? (
          <>
            <OddsLine tm="Over"  tmMuted val={total} />
            <OddsLine tm="Under" tmMuted val={total} />
          </>
        ) : (
          <div style={{
            color:'#7a828d', fontSize:'11px', textAlign:'center',
            padding:'10px 0',
            fontFamily:'Arial,sans-serif', letterSpacing:'0.3px',
          }}>
            O/U unavailable
          </div>
        )}
      </OddsCol>
    </div>
  )
}

function OddsCol({ label, isLast, children }) {
  return (
    <div style={{
      padding:'12px 14px',
      borderRight: isLast ? 'none' : '1px solid rgba(255,255,255,0.06)',
    }}>
      <div style={{
        fontSize:'10px', letterSpacing:'0.10em',
        color:'#aab2bd', fontWeight:700,
        marginBottom:'10px',
        fontFamily:'Arial,sans-serif',
      }}>
        {label}
      </div>
      {children}
    </div>
  )
}

function OddsLine({ tm, tmColor, tmMuted, val, valBold }) {
  return (
    <div style={{
      display:'flex', alignItems:'center', justifyContent:'space-between',
      fontSize:'13px', padding:'3px 0',
      fontFamily:'Arial,sans-serif',
      gap:'6px',
    }}>
      <span style={{ fontWeight:800, color: tmMuted ? '#aab2bd' : tmColor }}>{tm}</span>
      <span style={{ color:'#f4f6f8', fontWeight: valBold ? 700 : 500 }}>{val}</span>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// UFC HERO CARD — split two-card layout for UFC upcoming fights. Each fight
// is one UFCHeroCard with a red corner (left, away) and blue corner (right,
// home). Corner colors are FIXED by position — UFC has no team-color data;
// the red/blue corner convention is the genre's visual identity. Fighter
// silhouette webp images live in /public/ufc/, pre-split per corner color.
//
// Moneyline-row-with-empty-state per the build spec — UFC odds are all null
// today (getUFCFights bypasses the synth floor), so the row shows "Moneyline
// unavailable" and gracefully fills when a future MMA odds source lands.
// Spread + O/U deliberately omitted (meaningless for MMA today).
// ═══════════════════════════════════════════════════════════════════════════════

function UFCHeroCard({ game, pickedSide, pickStatus, locked, onPick, expanded, setExpanded }) {
  // Container border reflects pick state (mirrors HeroCard).
  const containerBorder =
    pickStatus === 'correct'   ? 'rgba(46,204,113,0.55)' :
    pickStatus === 'incorrect' ? 'rgba(255,59,59,0.45)'  :
    pickedSide                 ? 'rgba(0,230,118,0.20)'  : 'rgba(255,255,255,0.06)'

  return (
    <div style={{
      background: '#0d1016',
      border: `1px solid ${containerBorder}`,
      borderRadius: '18px',
      padding: '22px 22px 14px',
      marginBottom: '10px',
      boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
    }}>
      <UFCHeroTagRow weightClass={game.weightClass} roundName={game.roundName} roundPts={game.roundPts} />

      <HeroLockRow startTime={game.startTime} startTimeMs={game.startTimeMs} locked={locked} />

      {/* Matchup row — red corner (away, left) · VS hexagon · blue corner (home, right). */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr auto 1fr', alignItems:'center' }}>
        <FighterCorner
          fighter={game.away}
          cornerColor={UFC_CORNER_RED}
          imageUrl="/ufc/fighter-red.webp"
          glowPos="20% 30%"
          picked={pickedSide === 'away'}
          pickStatus={pickStatus}
          locked={locked}
          onClick={() => onPick('away')}
        />
        <VSHexagon />
        <FighterCorner
          fighter={game.home}
          cornerColor={UFC_CORNER_BLUE}
          imageUrl="/ufc/fighter-blue.webp"
          glowPos="80% 30%"
          picked={pickedSide === 'home'}
          pickStatus={pickStatus}
          locked={locked}
          onClick={() => onPick('home')}
        />
      </div>

      <UFCMoneylineRow
        awayML={game.awayML} homeML={game.homeML}
        awayLabel={game.away.shortName || game.away.team}
        homeLabel={game.home.shortName || game.home.team}
        awayCorner={UFC_CORNER_RED} homeCorner={UFC_CORNER_BLUE}
      />

      {/* Expand toggle — same state and downstream behavior as GameCard's. */}
      <button
        onClick={e => { e.stopPropagation(); setExpanded(x => !x) }}
        style={{
          width:'100%',
          background:'none', border:'none',
          borderTop:'1px solid rgba(255,255,255,0.06)',
          marginTop:'14px',
          paddingTop:'12px', paddingBottom:'4px',
          color: expanded ? 'var(--gold)' : '#aab2bd',
          fontSize:'13px', fontWeight:600,
          cursor:'pointer',
          display:'flex', alignItems:'center', justifyContent:'center', gap:'6px',
          fontFamily:'Arial,sans-serif', letterSpacing:'0.4px',
        }}
      >
        {expanded ? '▲ Hide Fighters' : '▼ Compare Fighters'}
      </button>

      {expanded && <TeamComparison game={game} />}
    </div>
  )
}

// Header tag row: `WEIGHT · POSITION · N PTS`. Each segment is conditional
// — ESPN sometimes omits weight class on prelims, and points are only
// shown for tiered fights (> 1pt). Dot separators inserted between rendered
// segments only.
function UFCHeroTagRow({ weightClass, roundName, roundPts }) {
  const segments = []
  if (weightClass) {
    segments.push(<span key="wc">{weightClass}</span>)
  }
  if (roundName) {
    segments.push(<span key="rn">{roundName}</span>)
  }
  if (roundPts > 1) {
    segments.push(
      <span key="rp" style={{
        ...ufcPtBadgeStyle(roundPts),
        padding:'3px 8px', borderRadius:'4px',
        fontWeight:'bold', fontSize:'10px', letterSpacing:'0.5px',
      }}>
        {roundPts} PT{roundPts !== 1 ? 'S' : ''}
      </span>
    )
  }
  if (segments.length === 0) return null

  const withDots = []
  segments.forEach((seg, i) => {
    if (i > 0) withDots.push(<span key={`dot-${i}`} style={{color:'#7a828d'}}>·</span>)
    withDots.push(seg)
  })

  return (
    <div style={{
      display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap',
      marginBottom:'14px',
      fontSize:'11px', fontWeight:700, letterSpacing:'0.08em',
      color:'#aab2bd', textTransform:'uppercase',
      fontFamily:'Arial,sans-serif',
    }}>
      {withDots}
    </div>
  )
}

function FighterCorner({ fighter, cornerColor, imageUrl, glowPos, picked, pickStatus, locked, onClick }) {
  const isCorrect = picked && pickStatus === 'correct'
  const isWrong   = picked && pickStatus === 'incorrect'
  const isPending = picked && pickStatus === 'pending'

  // cornerColor (red/blue) drives the structural border. Country-glow
  // and palette derivation now happen inside FighterPanelLayers via
  // the shared buildCountryGlow helper — caller passes country +
  // cornerColor, helper handles the palette lookup + fallback.
  const borderColor = isCorrect ? 'var(--green)' : isWrong ? 'var(--red)' : cornerColor

  // Fighter initial — same derivation the existing TeamLogo UFC branch uses,
  // imported from teamColors.js. shortName ("A. Carnelossi") is preferred;
  // displayName-based fallback covers fights where ESPN omits shortName.
  const initial = fighterInitial(fighter.shortName || fighter.team)

  const pickedBadge = picked && (
    isCorrect ? { ch: '✓', bg: 'var(--green)' } :
    isWrong   ? { ch: '✗', bg: 'var(--red)'   } :
    isPending ? { ch: '⏳', bg: 'var(--gold)'  } :
                { ch: '✓', bg: 'var(--gold)'  }
  )

  // Outer button uses the shared corner-tint bg + the shared layer
  // stack component. Same visual treatment as the Games-tab UFC cards
  // (single source of truth at src/lib/ufcFighterPanel.js + src/
  // components/FighterPanelLayers.jsx); tuning happens THERE, not at
  // the call sites.
  return (
    <button
      onClick={onClick}
      disabled={locked}
      style={{
        position:'relative',
        borderRadius:'16px',
        overflow:'hidden',
        cursor: locked ? 'default' : 'pointer',
        transition:'transform .12s ease',
        minHeight:'280px',
        width:'100%',
        background: ufcCornerTintBg(cornerColor),
        border: `1.5px solid ${borderColor}`,
        padding: 0,
        fontFamily:'Arial,sans-serif',
        display:'block',
        opacity: locked && !picked ? 0.7 : 1,
      }}
    >
      <FighterPanelLayers
        cornerColor={cornerColor}
        silhouetteUrl={imageUrl}
        flagUrl={fighter.flagUrl}
        country={fighter.country}
        side={cornerColor === UFC_CORNER_RED ? 'left' : 'right'}
      />

      {pickedBadge && (
        <span style={{
          position:'absolute', top:'10px', right:'10px',
          background: pickedBadge.bg,
          color:'#0d1016', borderRadius:'50%',
          width:'22px', height:'22px',
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:'12px', fontWeight:'bold',
          zIndex:5,
        }}>
          {pickedBadge.ch}
        </span>
      )}

      {/* Country flag badge — ESPN's MMA scoreboard hands us the flag
          PNG url directly. Sized for readability without crowding the
          picked-badge slot on the opposite corner. onError hides the
          element silently if ESPN's CDN ever drops the image. */}
      {fighter.flagUrl && (
        <img
          src={fighter.flagUrl}
          alt={fighter.country || ''}
          width="32"
          height="22"
          loading="lazy"
          onError={e => { e.currentTarget.style.display = 'none' }}
          style={{
            position:'absolute', top:'10px', left:'10px',
            borderRadius:'3px',
            objectFit:'cover',
            boxShadow:'0 1px 4px rgba(0,0,0,0.6)',
            zIndex:5,
          }}
        />
      )}

      {/* Name block — initial badge above name + corner-colored underline + record.
          Initial ring + underline → CORNER color (red left / blue right) per
          spec. Name fontSize bumped 16 → 19 for the mock's punchier hierarchy. */}
      <div style={{
        position:'absolute', bottom:'14px', left:'12px', right:'12px',
        zIndex:4, textAlign:'center',
      }}>
        <div style={{
          width:'42px', height:'42px', borderRadius:'50%',
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:'20px', fontWeight:800,
          background:'rgba(8,10,14,0.7)',
          border:`2px solid ${cornerColor}`,
          color:'#f4f6f8',
          margin:'0 auto 10px',
        }}>
          {initial}
        </div>
        <div style={{
          fontSize:'19px', fontWeight:900, textTransform:'uppercase',
          letterSpacing:'0.03em', lineHeight:1.05,
          color:'#f4f6f8',
          wordBreak:'break-word',
          // Reserve a 2-line-height slot so underline + record sit at
          // the same y across both corner cards regardless of whether
          // a name wraps. 42px ≈ 2 × 19px × 1.05. Bottom-justified so
          // a 1-line name hugs the underline.
          minHeight:'42px',
          display:'flex',
          flexDirection:'column',
          justifyContent:'flex-end',
        }}>
          {fighter.team}
        </div>
        <div style={{
          width:'40px', height:'3px', borderRadius:'2px',
          margin:'8px auto 0',
          background: cornerColor,
        }}/>
        {fighter.record && (
          <div style={{
            fontSize:'12px', color:'#aab2bd',
            marginTop:'8px',
            letterSpacing:'0.2px',
          }}>
            {fighter.record}
          </div>
        )}
      </div>

      {picked && !locked && (
        <div style={{
          position:'absolute', bottom:'2px', right:'8px',
          fontSize:'9px', color:'#7a828d',
          letterSpacing:'0.3px',
          zIndex:4,
        }}>
          ✏️ tap to change
        </div>
      )}
    </button>
  )
}

function VSHexagon() {
  return (
    <div style={{
      width:60, height:60,
      clipPath:'polygon(25% 5%, 75% 5%, 100% 50%, 75% 95%, 25% 95%, 0% 50%)',
      background:'#0d1016',
      display:'flex', alignItems:'center', justifyContent:'center',
      fontSize:'15px', fontWeight:800, letterSpacing:'0.05em',
      color:'#aab2bd',
      zIndex:5, margin:'0 -14px',
      fontFamily:'Arial,sans-serif',
    }}>
      VS
    </div>
  )
}

function UFCMoneylineRow({ awayML, homeML, awayLabel, homeLabel, awayCorner, homeCorner }) {
  // UFC odds are all null today (getUFCFights bypasses the synth floor
  // intentionally — a spread of -1.5 on a 2-fighter fight is nonsensical
  // and we correctly never invent one). When a future MMA odds source
  // lands and fills awayML/homeML, the populated branch renders both
  // moneylines; until then the empty state is the honest display.
  const both = awayML != null && homeML != null

  return (
    <div style={{
      marginTop:'16px',
      background:'#0b0e13',
      border:'1px solid rgba(255,255,255,0.06)',
      borderRadius:'14px',
      padding:'12px 16px',
    }}>
      <div style={{
        fontSize:'10px', letterSpacing:'0.10em',
        color:'#aab2bd', fontWeight:700,
        marginBottom:'10px',
        fontFamily:'Arial,sans-serif',
      }}>
        MONEYLINE
      </div>
      {both ? (
        <div style={{
          display:'flex', justifyContent:'space-between', alignItems:'center',
          fontSize:'14px',
          fontFamily:'Arial,sans-serif',
          gap:'12px',
        }}>
          <div style={{ display:'flex', alignItems:'center', gap:'8px', minWidth:0 }}>
            <span style={{ color: awayCorner, fontWeight:800, textTransform:'uppercase', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{awayLabel}</span>
            <span style={{ color:'#f4f6f8', fontWeight: awayML < 0 ? 700 : 500, flexShrink:0 }}>{fmtML(awayML)}</span>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:'8px', minWidth:0 }}>
            <span style={{ color:'#f4f6f8', fontWeight: homeML < 0 ? 700 : 500, flexShrink:0 }}>{fmtML(homeML)}</span>
            <span style={{ color: homeCorner, fontWeight:800, textTransform:'uppercase', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{homeLabel}</span>
          </div>
        </div>
      ) : (
        <div style={{
          color:'#7a828d', fontSize:'12px', textAlign:'center',
          padding:'4px 0 2px',
          fontFamily:'Arial,sans-serif', letterSpacing:'0.3px',
        }}>
          Moneyline unavailable
        </div>
      )}
    </div>
  )
}

// Odds column — spread (large) and moneyline (medium) for one team side
function OddsTeam({ abbr, spread, ml, isFav, isUnderdg, align, arrow }) {
  const mlColor  = isFav ? 'var(--gold)' : isUnderdg ? 'var(--green)' : 'var(--text-muted)'
  const spdColor = isFav ? 'var(--gold)' : 'var(--text)'
  const isRight  = align === 'right'
  const hasSpread = !!spread
  const hasML     = !!ml

  return (
    <div style={{ textAlign: align }}>
      {/* Team abbr + FAV badge */}
      <div style={{ display:'flex', alignItems:'center', gap:'4px', justifyContent: isRight ? 'flex-end' : 'flex-start', marginBottom:'4px' }}>
        {!isRight && isFav && <FavBadge />}
        <span style={{ fontSize:'11px', color:'var(--text-dim)', fontFamily:'Arial,sans-serif', fontWeight:'bold', letterSpacing:'0.5px' }}>{abbr}</span>
        {isRight && isFav && <FavBadge />}
      </div>
      {/* Spread — large */}
      <div style={{ fontSize:'20px', fontWeight:'bold', color: hasSpread ? spdColor : 'var(--text-muted)', fontFamily:'Arial,sans-serif', lineHeight:1 }}>
        {hasSpread ? spread : '—'}{arrow}
      </div>
      {/* Moneyline — medium */}
      <div style={{ fontSize:'15px', color: hasML ? mlColor : 'var(--text-muted)', fontFamily:'Arial,sans-serif', marginTop:'4px', fontWeight: isFav ? 'bold' : 'normal' }}>
        {hasML ? ml : '—'}
      </div>
    </div>
  )
}

function FavBadge() {
  return (
    <span style={{ fontSize:'9px', background:'var(--gold)', color:'#000', borderRadius:'3px', padding:'2px 5px', fontWeight:'bold', fontFamily:'Arial,sans-serif', letterSpacing:'0.5px', flexShrink:0 }}>FAV</span>
  )
}

function TeamButton({ teamObj, sport, side, pickedSide, pickStatus, locked, isLive, isFinal, pts, onClick }) {
  const isPicked  = pickedSide === side
  const isWrong   = isPicked && pickStatus === 'incorrect'
  const isCorrect = isPicked && pickStatus === 'correct'
  const isPending = isPicked && pickStatus === 'pending'
  const showScore = (isLive || isFinal) && teamObj.score !== null

  const borderColor = isCorrect ? 'var(--green)' : isWrong ? 'var(--red)' : isPicked ? 'var(--gold)' : 'var(--border)'
  const bgColor     = isCorrect ? 'rgba(46,204,113,0.1)' : isWrong ? 'rgba(255,59,59,0.08)' : isPicked ? 'var(--gold-bg2)' : 'var(--surface2)'
  const textColor   = isCorrect ? 'var(--green)' : isWrong ? 'var(--red)' : isPicked ? 'var(--gold)' : locked ? 'var(--text-dim)' : 'var(--text)'
  const badge       = isCorrect ? '✅' : isWrong ? '❌' : isPending ? '⏳' : null

  return (
    <button onClick={onClick} disabled={locked || isFinal}
      style={{ background:bgColor, border:`2px solid ${borderColor}`, borderRadius:'12px', padding:'12px 8px', cursor: locked || isFinal ? 'default' : 'pointer', transition:'all .18s', width:'100%', position:'relative', opacity: locked && !isPicked ? 0.6 : 1 }}
    >
      {badge && <span style={{ position:'absolute', top:'-9px', right:'-9px', fontSize:'14px', lineHeight:1 }}>{badge}</span>}
      {isPicked && !badge && (
        <span style={{ position:'absolute', top:'-8px', right:'-8px', background:'var(--gold)', color:'var(--bg)', borderRadius:'50%', width:'18px', height:'18px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'10px', fontWeight:'bold' }}>✓</span>
      )}
      {isPicked && !locked && !isFinal && !isLive && (
        <span style={{ position:'absolute', bottom:'4px', right:'6px', fontSize:'9px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif', letterSpacing:'0.3px' }}>
          ✏️ tap to change
        </span>
      )}

      <TeamLogo team={teamObj.team} abbr={teamObj.abbr} sport={sport} color={teamObj.color} alternateColor={teamObj.alternateColor} shortName={teamObj.shortName} size={36} borderColor={borderColor} isPicked={isPicked} />

      <div style={{ fontSize:'12px', fontWeight: isPicked ? 'bold' : 'normal', color:textColor, marginBottom:'3px', lineHeight:'1.3', overflow:'hidden', textOverflow:'ellipsis', display:'flex', alignItems:'center', justifyContent:'center', gap:'4px' }}>
        {teamObj.seed != null && (
          <span style={{ fontSize:'10px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif', fontWeight:'normal' }}>#{teamObj.seed}</span>
        )}
        <span style={{ overflow:'hidden', textOverflow:'ellipsis' }}>{teamObj.team}</span>
      </div>

      {teamObj.record && !showScore && (
        <div style={{ fontSize:'10px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif' }}>{teamObj.record}</div>
      )}

      {showScore && (
        <div style={{ fontSize:'22px', fontWeight:'bold', color:textColor, fontFamily:'Arial,sans-serif', marginTop:'4px' }}>{teamObj.score}</div>
      )}

      {isPicked && pickStatus === 'correct'   && <div style={{ fontSize:'11px', color:'var(--green)', marginTop:'3px', fontFamily:'Arial,sans-serif', fontWeight:'bold' }}>+{pts}pt</div>}
      {isPicked && pickStatus === 'incorrect' && <div style={{ fontSize:'11px', color:'var(--red)',   marginTop:'3px', fontFamily:'Arial,sans-serif' }}>+0pt</div>}
      {isPicked && pickStatus === 'pending'   && <div style={{ fontSize:'11px', color:'var(--text-muted)', marginTop:'3px' }}>pending</div>}
    </button>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// FULL BOX SCORE
// ═══════════════════════════════════════════════════════════════════════════════

function BoxScore({ game }) {
  const [teamOpen, setTeamOpen] = useState(null)
  const bs = getBoxScore(game.id)
  const awayWon = game.away.score > game.home.score
  const homeWon = game.home.score > game.away.score

  // Simple fallback if no detailed data
  if (!bs) return (
    <div style={{ paddingTop:'12px' }}>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 48px 1fr', gap:'8px', textAlign:'center' }}>
        <div>
          <div style={{ fontSize:'11px', color:'var(--text-dim)', fontFamily:'Arial,sans-serif', marginBottom:'4px' }}>{game.away.abbr}</div>
          <div style={{ fontSize:'30px', fontWeight:'bold', fontFamily:'Arial,sans-serif', color: awayWon ? 'var(--gold)' : 'var(--text-dim)' }}>{game.away.score ?? '—'}</div>
          {awayWon && <div style={{ fontSize:'10px', color:'var(--green)', fontWeight:'bold', marginTop:'2px', fontFamily:'Arial,sans-serif' }}>WINNER</div>}
        </div>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ fontSize:'10px', fontFamily:'Arial,sans-serif', color:'var(--text-muted)', fontWeight:'bold' }}>FINAL</div>
        </div>
        <div>
          <div style={{ fontSize:'11px', color:'var(--text-dim)', fontFamily:'Arial,sans-serif', marginBottom:'4px' }}>{game.home.abbr}</div>
          <div style={{ fontSize:'30px', fontWeight:'bold', fontFamily:'Arial,sans-serif', color: homeWon ? 'var(--gold)' : 'var(--text-dim)' }}>{game.home.score ?? '—'}</div>
          {homeWon && <div style={{ fontSize:'10px', color:'var(--green)', fontWeight:'bold', marginTop:'2px', fontFamily:'Arial,sans-serif' }}>WINNER</div>}
        </div>
      </div>
      {game.seriesInfo && <div style={{ textAlign:'center', marginTop:'10px', fontSize:'12px', color:'var(--gold)', fontWeight:'bold' }}>{game.seriesInfo}</div>}
    </div>
  )

  return (
    <div style={{ paddingTop:'10px' }}>
      {/* Score header */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 56px 1fr', textAlign:'center', marginBottom:'12px', gap:'4px' }}>
        <div>
          <div style={{ fontSize:'11px', color:'var(--text-dim)', fontFamily:'Arial,sans-serif', marginBottom:'2px' }}>{game.away.abbr}</div>
          <div style={{ fontSize:'32px', fontWeight:'bold', fontFamily:'Arial,sans-serif', color: awayWon ? 'var(--gold)' : 'var(--text-dim)', lineHeight:1 }}>{game.away.score}</div>
          {awayWon && <div style={{ fontSize:'10px', color:'var(--green)', fontWeight:'bold', marginTop:'3px', fontFamily:'Arial,sans-serif', letterSpacing:'0.5px' }}>WINNER</div>}
          {game.away.record && <div style={{ fontSize:'10px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif', marginTop:'2px' }}>{game.away.record}</div>}
        </div>
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
          <div style={{ fontSize:'10px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif', fontWeight:'bold', letterSpacing:'0.5px' }}>FINAL</div>
          {game.seriesInfo && <div style={{ fontSize:'9px', color:'var(--gold)', marginTop:'4px', textAlign:'center', lineHeight:'1.3' }}>{game.seriesInfo}</div>}
        </div>
        <div>
          <div style={{ fontSize:'11px', color:'var(--text-dim)', fontFamily:'Arial,sans-serif', marginBottom:'2px' }}>{game.home.abbr}</div>
          <div style={{ fontSize:'32px', fontWeight:'bold', fontFamily:'Arial,sans-serif', color: homeWon ? 'var(--gold)' : 'var(--text-dim)', lineHeight:1 }}>{game.home.score}</div>
          {homeWon && <div style={{ fontSize:'10px', color:'var(--green)', fontWeight:'bold', marginTop:'3px', fontFamily:'Arial,sans-serif', letterSpacing:'0.5px' }}>WINNER</div>}
          {game.home.record && <div style={{ fontSize:'10px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif', marginTop:'2px' }}>{game.home.record}</div>}
        </div>
      </div>

      {/* Period table */}
      <BsPeriodTable bs={bs} game={game} />

      {/* Team stats */}
      <BsTeamStats bs={bs} game={game} />

      {/* Player sections */}
      <BsPlayerSection
        label={`${game.away.abbr} Players`}
        players={bs.awayPlayers}
        sport={bs.sport}
        open={teamOpen === 'away'}
        onToggle={() => setTeamOpen(teamOpen === 'away' ? null : 'away')}
      />
      <BsPlayerSection
        label={`${game.home.abbr} Players`}
        players={bs.homePlayers}
        sport={bs.sport}
        open={teamOpen === 'home'}
        onToggle={() => setTeamOpen(teamOpen === 'home' ? null : 'home')}
      />
    </div>
  )
}

function BsPeriodTable({ bs, game }) {
  const isMLB = bs.sport === 'MLB'
  const rowStyle = (side, i) => ({
    background: i % 2 === 0 ? 'rgba(255,255,255,0.03)' : 'transparent',
  })
  const cellStyle = { padding:'4px 3px', textAlign:'center', fontSize:'12px', fontFamily:'Arial,sans-serif' }
  const headStyle = { ...cellStyle, fontSize:'10px', color:'var(--text-muted)' }

  return (
    <div style={{ marginBottom:'10px', overflowX:'auto' }}>
      <div style={{ fontSize:'9px', color:'var(--text-muted)', letterSpacing:'1px', marginBottom:'5px', fontFamily:'Arial,sans-serif' }}>SCORING</div>
      <table style={{ width:'100%', borderCollapse:'collapse', minWidth: isMLB ? '290px' : 'auto' }}>
        <thead>
          <tr>
            <th style={{ ...headStyle, textAlign:'left', width:'32px' }}></th>
            {bs.periods.map(p => <th key={p.label} style={headStyle}>{p.label}</th>)}
            {isMLB ? (
              <>
                <th style={{ ...headStyle, fontWeight:'bold', color:'var(--text-dim)' }}>R</th>
                <th style={headStyle}>H</th>
                <th style={headStyle}>E</th>
              </>
            ) : <th style={{ ...headStyle, fontWeight:'bold', color:'var(--text-dim)' }}>T</th>}
          </tr>
        </thead>
        <tbody>
          {[['away', game.away], ['home', game.home]].map(([side, t], si) => (
            <tr key={side} style={rowStyle(side, si)}>
              <td style={{ ...cellStyle, textAlign:'left', color:'var(--gold)', fontWeight:'bold', fontSize:'11px' }}>{t.abbr}</td>
              {bs.periods.map((p, pi) => (
                <td key={pi} style={cellStyle}>{side === 'away' ? p.away : p.home}</td>
              ))}
              {isMLB ? (
                <>
                  <td style={{ ...cellStyle, color:'var(--gold)', fontWeight:'bold' }}>{bs[side + 'Team'].r}</td>
                  <td style={cellStyle}>{bs[side + 'Team'].h}</td>
                  <td style={cellStyle}>{bs[side + 'Team'].e}</td>
                </>
              ) : <td style={{ ...cellStyle, color:'var(--gold)', fontWeight:'bold' }}>{t.score}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function BsTeamStats({ bs, game }) {
  const sport = bs.sport
  const defs = sport === 'NBA' ? [
    ['FG%',  bs.awayTeam.fgPct  + '%', bs.homeTeam.fgPct  + '%'],
    ['3PT%', bs.awayTeam.tptPct + '%', bs.homeTeam.tptPct + '%'],
    ['REB',  bs.awayTeam.reb,          bs.homeTeam.reb         ],
    ['AST',  bs.awayTeam.ast,          bs.homeTeam.ast         ],
    ['TO',   bs.awayTeam.to,           bs.homeTeam.to          ],
    ['STL',  bs.awayTeam.stl,          bs.homeTeam.stl         ],
  ] : sport === 'MLB' ? [
    ['AVG', '.' + String(Math.round(bs.awayTeam.avg * 1000)).padStart(3,'0'), '.' + String(Math.round(bs.homeTeam.avg * 1000)).padStart(3,'0')],
    ['LOB', bs.awayTeam.lob, bs.homeTeam.lob],
  ] : [
    ['SOG',  bs.awayTeam.sog,      bs.homeTeam.sog     ],
    ['FO%',  bs.awayTeam.foWPct + '%', bs.homeTeam.foWPct + '%'],
    ['PPG',  bs.awayTeam.ppg,      bs.homeTeam.ppg     ],
    ['HITS', bs.awayTeam.hits,     bs.homeTeam.hits    ],
    ['BLK',  bs.awayTeam.blk,      bs.homeTeam.blk     ],
  ]

  return (
    <div style={{ background:'var(--surface2)', borderRadius:'8px', padding:'10px 10px 6px', marginBottom:'10px' }}>
      <div style={{ display:'grid', gridTemplateColumns:'1fr auto 1fr', marginBottom:'8px' }}>
        <span style={{ fontSize:'12px', fontWeight:'bold', color:'var(--gold)', fontFamily:'Arial,sans-serif' }}>{game.away.abbr}</span>
        <span style={{ fontSize:'9px', color:'var(--text-muted)', letterSpacing:'1px', fontFamily:'Arial,sans-serif', textAlign:'center', alignSelf:'center' }}>TEAM STATS</span>
        <span style={{ fontSize:'12px', fontWeight:'bold', color:'var(--gold)', fontFamily:'Arial,sans-serif', textAlign:'right' }}>{game.home.abbr}</span>
      </div>
      {defs.map(([label, av, hv]) => (
        <div key={label} style={{ display:'grid', gridTemplateColumns:'1fr 48px 1fr', gap:'4px', alignItems:'center', marginBottom:'4px' }}>
          <div style={{ fontSize:'13px', fontFamily:'Arial,sans-serif', textAlign:'left', color:'var(--text)' }}>{av}</div>
          <div style={{ fontSize:'10px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif', textAlign:'center', whiteSpace:'nowrap' }}>{label}</div>
          <div style={{ fontSize:'13px', fontFamily:'Arial,sans-serif', textAlign:'right', color:'var(--text)' }}>{hv}</div>
        </div>
      ))}
    </div>
  )
}

function BsPlayerSection({ label, players, sport, open, onToggle }) {
  const pitchers = players.filter(p => p.role === 'pitcher')
  const batters  = players.filter(p => p.role === 'batter')
  const skaters  = players.filter(p => p.role === 'skater')
  const goalies  = players.filter(p => p.role === 'goalie')

  const nameCell = { fontSize:'12px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'100%' }
  const numCell  = { fontSize:'12px', textAlign:'center', fontFamily:'Arial,sans-serif' }
  const hdrCell  = { fontSize:'10px', color:'var(--text-muted)', textAlign:'center', fontFamily:'Arial,sans-serif' }

  return (
    <div style={{ marginBottom:'8px', border:'1px solid var(--border)', borderRadius:'8px', overflow:'hidden' }}>
      <button onClick={onToggle} style={{ width:'100%', background:'var(--surface2)', border:'none', padding:'9px 12px', display:'flex', justifyContent:'space-between', alignItems:'center', cursor:'pointer' }}>
        <span style={{ fontSize:'12px', fontWeight:'bold', color:'var(--gold)', fontFamily:'Arial,sans-serif' }}>{label}</span>
        <span style={{ fontSize:'11px', color:'var(--text-muted)' }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{ padding:'10px 10px 6px', overflowX:'auto' }}>

          {/* NBA */}
          {sport === 'NBA' && (
            <>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 32px 32px 32px 44px 44px 36px', gap:'3px', marginBottom:'4px' }}>
                <div style={{ fontSize:'10px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif' }}>PLAYER</div>
                {['PTS','REB','AST','FG','3PT','+/-'].map(h => <div key={h} style={hdrCell}>{h}</div>)}
              </div>
              {players.map((p, i) => (
                <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 32px 32px 32px 44px 44px 36px', gap:'3px', padding:'4px 0', borderTop:'1px solid var(--border)' }}>
                  <div style={{ ...nameCell, color: p.highlight ? 'var(--gold)' : 'var(--text)' }}>{p.name}</div>
                  <div style={{ ...numCell, fontWeight: p.pts >= 30 ? 'bold' : 'normal', color: p.pts >= 30 ? 'var(--gold)' : 'var(--text)' }}>{p.pts}</div>
                  <div style={numCell}>{p.reb}</div>
                  <div style={numCell}>{p.ast}</div>
                  <div style={{ ...numCell, fontSize:'11px', color:'var(--text-dim)' }}>{p.fg}</div>
                  <div style={{ ...numCell, fontSize:'11px', color:'var(--text-dim)' }}>{p.tpt}</div>
                  <div style={{ ...numCell, color: p.pm.startsWith('+') ? 'var(--green)' : 'var(--red)' }}>{p.pm}</div>
                </div>
              ))}
            </>
          )}

          {/* MLB */}
          {sport === 'MLB' && (
            <>
              {pitchers.length > 0 && (
                <>
                  <div style={{ fontSize:'9px', color:'var(--text-muted)', letterSpacing:'1px', marginBottom:'4px', fontFamily:'Arial,sans-serif' }}>PITCHING</div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 28px 22px 22px 22px 22px 40px', gap:'3px', marginBottom:'3px' }}>
                    <div style={{ fontSize:'10px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif' }}>PITCHER</div>
                    {['IP','H','ER','K','BB','ERA'].map(h => <div key={h} style={hdrCell}>{h}</div>)}
                  </div>
                  {pitchers.map((p, i) => (
                    <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 28px 22px 22px 22px 22px 40px', gap:'3px', padding:'4px 0', borderTop:'1px solid var(--border)' }}>
                      <div style={nameCell}>{p.name}</div>
                      <div style={numCell}>{p.ip}</div>
                      <div style={numCell}>{p.h}</div>
                      <div style={numCell}>{p.er}</div>
                      <div style={numCell}>{p.k}</div>
                      <div style={numCell}>{p.bb}</div>
                      <div style={{ ...numCell, fontSize:'11px', color:'var(--text-dim)' }}>{p.era.toFixed(2)}</div>
                    </div>
                  ))}
                </>
              )}
              {batters.length > 0 && (
                <>
                  <div style={{ fontSize:'9px', color:'var(--text-muted)', letterSpacing:'1px', margin:'8px 0 4px', fontFamily:'Arial,sans-serif' }}>BATTING</div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 26px 22px 28px 22px 40px', gap:'3px', marginBottom:'3px' }}>
                    <div style={{ fontSize:'10px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif' }}>BATTER</div>
                    {['AB','H','RBI','HR','AVG'].map(h => <div key={h} style={hdrCell}>{h}</div>)}
                  </div>
                  {batters.map((p, i) => (
                    <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 26px 22px 28px 22px 40px', gap:'3px', padding:'4px 0', borderTop:'1px solid var(--border)' }}>
                      <div style={{ ...nameCell, color: p.highlight ? 'var(--gold)' : 'var(--text)' }}>{p.name}</div>
                      <div style={numCell}>{p.ab}</div>
                      <div style={numCell}>{p.h}</div>
                      <div style={numCell}>{p.rbi}</div>
                      <div style={{ ...numCell, color: p.hr > 0 ? 'var(--gold)' : 'var(--text-dim)' }}>{p.hr > 0 ? p.hr : '–'}</div>
                      <div style={{ ...numCell, fontSize:'11px', color:'var(--text-dim)' }}>.{String(Math.round(p.avg * 1000)).padStart(3,'0')}</div>
                    </div>
                  ))}
                </>
              )}
            </>
          )}

          {/* NHL */}
          {sport === 'NHL' && (
            <>
              {skaters.length > 0 && (
                <>
                  <div style={{ fontSize:'9px', color:'var(--text-muted)', letterSpacing:'1px', marginBottom:'4px', fontFamily:'Arial,sans-serif' }}>SKATERS</div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 22px 22px 26px 30px 28px 40px', gap:'3px', marginBottom:'3px' }}>
                    <div style={{ fontSize:'10px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif' }}>PLAYER</div>
                    {['G','A','PTS','+/-','SOG','TOI'].map(h => <div key={h} style={hdrCell}>{h}</div>)}
                  </div>
                  {skaters.map((p, i) => (
                    <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 22px 22px 26px 30px 28px 40px', gap:'3px', padding:'4px 0', borderTop:'1px solid var(--border)' }}>
                      <div style={{ ...nameCell, color: p.highlight ? 'var(--gold)' : 'var(--text)' }}>{p.name}</div>
                      <div style={{ ...numCell, color: p.g > 0 ? 'var(--gold)' : 'var(--text-dim)' }}>{p.g}</div>
                      <div style={numCell}>{p.a}</div>
                      <div style={{ ...numCell, fontWeight:'bold' }}>{p.pts}</div>
                      <div style={{ ...numCell, color: p.pm > 0 ? 'var(--green)' : p.pm < 0 ? 'var(--red)' : 'var(--text-dim)' }}>{p.pm > 0 ? `+${p.pm}` : p.pm}</div>
                      <div style={numCell}>{p.sog}</div>
                      <div style={{ ...numCell, fontSize:'10px', color:'var(--text-dim)' }}>{p.toi}</div>
                    </div>
                  ))}
                </>
              )}
              {goalies.length > 0 && (
                <>
                  <div style={{ fontSize:'9px', color:'var(--text-muted)', letterSpacing:'1px', margin:'8px 0 4px', fontFamily:'Arial,sans-serif' }}>GOALTENDER</div>
                  {goalies.map((p, i) => (
                    <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'5px 0', borderTop:'1px solid var(--border)', gap:'8px' }}>
                      <span style={{ fontSize:'12px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.name}</span>
                      <span style={{ fontSize:'12px', fontFamily:'Arial,sans-serif', color: p.svPct >= .900 ? 'var(--gold)' : 'var(--text-dim)', flexShrink:0 }}>
                        {p.sv}/{p.sa} · {(p.svPct * 100).toFixed(1)}%
                      </span>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
